import useVisibleProjects from "../hooks/useVisibleProjects";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, deleteDoc, doc, getDocs, onSnapshot, serverTimestamp, writeBatch } from "firebase/firestore";
import { FaCalendarAlt, FaCheck, FaMagic, FaMicrophone, FaPaperPlane, FaPen, FaQuestionCircle, FaRedoAlt, FaTimes, FaTrash } from "react-icons/fa";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";
import LoadingIndicator from "../components/LoadingIndicator";

import { persistVoiceFollowup } from "../utils/voiceDraftPersistence";
import { validateSlot } from "../realtime/scheduleTools";
import useVoiceConversation from "../hooks/useVoiceConversation";

import { reusableShifts } from "../utils/reusableShifts";
import { filterEmployeesForSchema, mergeSchedulingEmployees, readSchedulingOwner } from "../utils/schedulingPopupData";

export function QuickShiftPanel({ companyUid, employees, projects, initialEmployeeId, onSelect }) {
  const [employeeId, setEmployeeId] = useState(initialEmployeeId || "");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    if (employeeId && !employees.some((item) => item.id === employeeId)) setEmployeeId("");
  }, [employeeId, employees]);
  useEffect(() => {
    setHistory([]); setLoading(true); setError("");
    if (!companyUid) { setLoading(false); return; }
    return onSnapshot(collection(db, "users", companyUid, "shifts"), (snapshot) => {
      setHistory(snapshot.docs.map((item) => item.data())); setLoading(false);
    }, () => { setError("Tidigare pass kunde inte hämtas. Försök öppna panelen igen."); setLoading(false); });
  }, [companyUid]);
  const employee = employees.find((item) => [item.id, item.uid, item.userId, item.employeeId].includes(employeeId));
  const templates = useMemo(() => reusableShifts(history, projects, employee), [history, projects, employee]);
  const [stableList, setStableList] = useState({ companyUid, employeeId, items: [] });
  useEffect(() => {
    setStableList((previous) => {
      if (previous.companyUid !== companyUid || previous.employeeId !== employeeId) {
        return { companyUid, employeeId, items: templates };
      }
      // Behåll kortens plats när ett återanvänt pass uppdaterar historiken.
      const remaining = new Map(templates.map((item) => [item.key, item]));
      const items = previous.items.flatMap((item) => {
        const current = remaining.get(item.key);
        remaining.delete(item.key);
        return current ? [current] : [];
      });
      return { companyUid, employeeId, items: [...items, ...remaining.values()] };
    });
  }, [templates, companyUid, employeeId]);
  const cards = stableList.items.filter((item) => `${item.name} ${item.startTime} ${item.endTime}`.toLocaleLowerCase("sv").includes(search.toLocaleLowerCase("sv")));
  const payload = (item) => ({ id: item.id, name: item.name, startTime: item.startTime, endTime: item.endTime, breaks: item.breaks || [], templateEmployeeId: employee?.id || "" });
  return <section className="schedule-fetcher quick-shifts" aria-label="Återanvänd pass">
    <strong>Återanvänd pass</strong>
    <span>Välj person och dra ett pass till en dag i schemat.</span>
    <label>Person<select value={employeeId} onChange={(event) => { setEmployeeId(event.target.value); onSelect(null); }}><option value="">Alla personers pass</option>{employees.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
    <small>{employee ? `${employee.name}s tidigare pass, vanligaste först.` : "Tidigare pass för hela företaget, vanligaste först."} Projekt och tider hämtas automatiskt. På en personrad får den personen passet.</small>
    <label>Sök pass<input type="search" value={search} placeholder="Projekt eller tid…" onChange={(event) => setSearch(event.target.value)} /></label>
    {loading && <LoadingIndicator message="Hämtar tidigare pass..." />}
    {error && <span role="alert">{error}</span>}
    {!loading && !error && !cards.length && <span>{search ? "Inga pass matchar sökningen." : "Inga tidigare pass hittades. Sparade pass visas här automatiskt."}</span>}
    <div className="quick-shift-cards">{cards.map((item) => <button type="button" key={item.key} draggable onDragStart={(event) => { event.dataTransfer.setData("application/x-schema-project", JSON.stringify(payload(item))); event.dataTransfer.effectAllowed = "copy"; }} onClick={() => onSelect(payload(item))} className="quick-shift-card" aria-label={`Välj ${item.name} ${item.startTime}–${item.endTime}${item.breaks?.length ? `, rast ${item.breaks.map((pause) => pause.appliesToWholeShift ? "hela passet" : `${pause.startTime || "?"}–${pause.endTime || "?"}`).join(", ")}` : ""}`}><strong>{item.name}</strong><span>{item.startTime}–{item.endTime}</span>{item.breaks?.map((pause, index) => <span className="quick-shift-break" key={`${pause.startTime || "whole"}-${pause.endTime || ""}-${index}`}><strong>Rast</strong> {pause.appliesToWholeShift ? "Hela passet" : `${pause.startTime || "?"}–${pause.endTime || "?"}`}</span>)}</button>)}</div>
    <style>{`.quick-shifts>span,.quick-shifts small{font-size:12px;line-height:1.5;color:#64748b}.quick-shift-cards{display:grid;gap:8px}.quick-shifts .quick-shift-card{display:flex;flex-direction:column;align-items:flex-start;gap:4px;border:1px solid #ddd6fe;border-left:4px solid #7c3aed;border-radius:10px;padding:12px;background:#fff;cursor:grab;text-align:left;color:#334155}.quick-shift-card:hover{background:#f5f3ff}.quick-shift-card:active{cursor:grabbing}.quick-shifts .quick-shift-card .quick-shift-break{color:#64748b;font-size:.9em}.quick-shifts .quick-shift-card .quick-shift-break strong{display:inline;font-size:inherit;color:#334155}.quick-shifts input{box-sizing:border-box;min-width:0;width:100%;padding:8px;border:1px solid #cbd5e1;border-radius:7px}`}</style>
  </section>;
}

const MONTHS = {
  januari: 1, februari: 2, mars: 3, april: 4, maj: 5, juni: 6,
  juli: 7, augusti: 8, september: 9, oktober: 10, november: 11, december: 12,
};

const MONTH_ALIASES = {
  jan: 1, feb: 2, mar: 3, apr: 4, jun: 6, jul: 7, aug: 8,
  sep: 9, sept: 9, okt: 10, nov: 11, dec: 12,
};

const UNSTAFFED_EMPLOYEE = { id: "__unassigned__", name: "Obemannad", isObemannad: true };

const normalize = (value) => String(value || "")
  .toLocaleLowerCase("sv-SE")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, " ")
  .trim();

const employeeName = (data) =>
  data.firstName && data.lastName
    ? `${data.firstName} ${data.lastName}`
    : data.name || data.displayName || data.userName || "Användare";

export function findAllMentions(items, message, getName) {
  const exactMatches = items.filter((item) => {
    const name = normalize(getName(item));
    return name && ` ${normalize(message)} `.includes(` ${name} `);
  });
  // En exakt träff ska alltid vinna. Fortsatt fuzzy-matchning här kunde göra
  // att exempelvis det exakta projektet "BK" fick med sig ett annat projekt.
  if (exactMatches.length) return exactMatches;
  const messageWords = normalize(message).split(" ").filter(Boolean);
  const fuzzyMatches = items.filter((item) => {
    const nameWords = normalize(getName(item)).split(" ").filter(Boolean);
    const matched = nameWords.filter((nameWord) => messageWords.some((word) => {
      if (word.length < 3) return word === nameWord;
      return editDistance(word, nameWord) <= Math.max(1, Math.floor(nameWord.length * 0.25));
    })).length;
    return matched >= Math.min(2, nameWords.length);
  });
  return fuzzyMatches;
}

// Rangordna hela namn tillsammans; projekt använder fortsatt sin egen matchning.
export function findClosestEmployees(employees, message) {
  const spoken = normalize(message);
  const exact = employees.filter((employee) => {
    const name = normalize(employee.name);
    return name && ` ${spoken} `.includes(` ${name} `);
  });
  if (exact.length) return exact;
  const words = spoken.split(" ").filter(Boolean);
  const sound = (word) => word.replace(/(?:tz|ts|z)/g, "s").replace(/ph/g, "f").replace(/([a-z])\1+/g, "$1");
  const ranked = employees.map((employee) => {
    const nameWords = normalize(employee.name).split(" ").filter(Boolean);
    let best = Infinity;
    for (let start = 0; nameWords.length && start <= words.length - nameWords.length; start += 1) {
      let score = 0;
      for (let index = 0; index < nameWords.length; index += 1) {
        const name = nameWords[index], word = words[start + index];
        const distance = Math.min(editDistance(name, word), editDistance(sound(name), sound(word)));
        if ((Math.min(name.length, word.length) < 3 && name !== word) || distance > Math.max(1, Math.floor(name.length * 0.3))) {
          score = Infinity; break;
        }
        score += distance / Math.max(name.length, word.length);
      }
      best = Math.min(best, score);
    }
    return { employee, score: best };
  }).filter((item) => Number.isFinite(item.score)).sort((left, right) => left.score - right.score);
  if (!ranked.length) return [];
  // Mycket närliggande resultat behålls så chatten kan be om förtydligande.
  return ranked.filter((item) => item.score <= ranked[0].score + 0.05).map((item) => item.employee);
}

function editDistance(left, right) {
  const rows = Array.from({ length: left.length + 1 }, (_, index) => [index]);
  for (let column = 0; column <= right.length; column += 1) rows[0][column] = column;
  for (let row = 1; row <= left.length; row += 1) {
    for (let column = 1; column <= right.length; column += 1) {
      rows[row][column] = Math.min(
        rows[row - 1][column] + 1,
        rows[row][column - 1] + 1,
        rows[row - 1][column - 1] + (left[row - 1] === right[column - 1] ? 0 : 1)
      );
    }
  }
  return rows[left.length][right.length];
}

function parseDates(message, fallbackDate) {
  const normalized = normalize(String(message).replace(/\b(?:[01]?\d|2[0-3])[:.]\d{2}\b/g, " "));
  const results = [];
  const addDate = (year, month, day) => {
    const candidate = new Date(year, month - 1, day);
    if (candidate.getFullYear() === year && candidate.getMonth() === month - 1 && candidate.getDate() === day) {
      results.push(`${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`);
    }
  };
  for (const iso of normalized.matchAll(/\b(20\d{2})[ -](\d{1,2})[ -](\d{1,2})\b/g)) addDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  for (const numeric of normalized.matchAll(/\b(\d{1,2})[/-](\d{1,2})(?:[/-](20\d{2}))?\b/g)) {
    addDate(Number(numeric[3]) || fallbackDate.getFullYear(), Number(numeric[2]), Number(numeric[1]));
  }

  const allMonths = { ...MONTHS, ...MONTH_ALIASES };
  const monthNames = Object.keys(allMonths).join("|");
  const yearFallback = fallbackDate.getFullYear();
  const dayList = new RegExp(`\\b((?:\\d{1,2}(?:a|e)?\\s*(?:,|och)\\s*)+\\d{1,2}(?:a|e)?)\\s+(${monthNames})(?:\\s+(20\\d{2}))?\\b`, "g");
  for (const match of normalized.matchAll(dayList)) {
    const year = Number(match[3]) || yearFallback;
    for (const day of match[1].match(/\d{1,2}/g) || []) addDate(year, allMonths[match[2]], Number(day));
  }
  const dayFirst = new RegExp(`\\b(\\d{1,2})(?:a|e)?(?:\\s+(?:och|,)?\\s*(\\d{1,2})(?:a|e)?)?\\s+(?:[a-zåäö]{1,3}\\s+)?(${monthNames})(?:\\s+(20\\d{2}))?\\b`, "g");
  for (const match of normalized.matchAll(dayFirst)) {
    const year = Number(match[4]) || yearFallback;
    addDate(year, allMonths[match[3]], Number(match[1]));
    if (match[2]) addDate(year, allMonths[match[3]], Number(match[2]));
  }
  const monthFirst = new RegExp(`\\b(${monthNames})\\s+(\\d{1,2})(?:a|e)?(?:\\s+(?:och|,)?\\s*(\\d{1,2})(?:a|e)?)?(?:\\s+(20\\d{2}))?\\b`, "g");
  for (const match of normalized.matchAll(monthFirst)) {
    const year = Number(match[4]) || yearFallback;
    addDate(year, allMonths[match[1]], Number(match[2]));
    if (match[3]) addDate(year, allMonths[match[1]], Number(match[3]));
  }
  if (!results.length) {
    const mentionedMonth = Object.keys(MONTHS).find((monthName) =>
      new RegExp(`\\b${monthName}\\b`).test(normalized)
    );
    if (mentionedMonth) {
      addDate(yearFallback, allMonths[mentionedMonth], fallbackDate.getDate());
    }
  }
  return [...new Set(results)];
}

function referencedDraftDates(message, currentDraft, fallbackDate) {
  const explicitDates = parseDates(message, fallbackDate);
  if (explicitDates.length) return explicitDates;
  const normalizedMessage = normalize(
    String(message).replace(/\b(?:[01]?\d|2[0-3])[:.]\d{2}\b/g, " ")
  );
  const days = [];
  for (const match of normalizedMessage.matchAll(/\b(?:den|dag(?:en)?|pa|i)\s+(\d{1,2})(?:a|e)?(?:\s+och\s+(\d{1,2})(?:a|e)?)?/g)) {
    days.push(Number(match[1]));
    if (match[2]) days.push(Number(match[2]));
  }
  if (!days.length) {
    const commandDays = normalizedMessage.match(/\b(?:ta bort|ta bot|radera|stryk|avboka)\s+(\d{1,2})(?:\s+och\s+(\d{1,2}))?/);
    if (commandDays) {
      days.push(Number(commandDays[1]));
      if (commandDays[2]) days.push(Number(commandDays[2]));
    }
  }
  const referenceDate = currentDraft?.rows?.at(-1)?.date;
  if (!referenceDate) return [];
  const [year, month] = referenceDate.split("-").map(Number);
  return [...new Set(days)].flatMap((day) => {
    const candidate = new Date(year, month - 1, day);
    return candidate.getMonth() === month - 1
      ? [`${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`]
      : [];
  });
}

function parseTimes(message) {
  const range = String(message).match(/\b([01]?\d|2[0-3])(?:[:.]([0-5]\d))?\s*(?:[-–—]|till|til)\s*([01]?\d|2[0-3])(?:[:.]([0-5]\d))?\b/i);
  if (range) return {
    startTime: `${String(range[1]).padStart(2, "0")}:${range[2] || "00"}`,
    endTime: `${String(range[3]).padStart(2, "0")}:${range[4] || "00"}`,
  };

  const matches = [...String(message).matchAll(/(?:^|\s)([01]?\d|2[0-3])(?:[:.]([0-5]\d))?(?=\s|$|\s*[-–—])/g)]
    .filter((match) => match[2])
    .map((match) => `${String(match[1]).padStart(2, "0")}:${match[2]}`);
  if (matches.length >= 2) return { startTime: matches[0], endTime: matches[1] };
  return { startTime: "", endTime: "" };
}

function parseWorkBlocks(message, projects) {
  const rangePattern = /\b([01]?\d|2[0-3])(?:[:.]([0-5]\d))?\s*(?:-|–|—|till|til)\s*([01]?\d|2[0-3])(?:[:.]([0-5]\d))?\b/gi;
  let ranges = [...String(message).matchAll(rangePattern)];
  if (!ranges.length) {
    const adjacentTimesPattern = /\b([01]?\d|2[0-3])[:.]([0-5]\d)\s+([01]?\d|2[0-3])[:.]([0-5]\d)\b/gi;
    ranges = [...String(message).matchAll(adjacentTimesPattern)];
  }
  if (!ranges.length) return [];

  return ranges.map((range, index) => {
    const segmentStart = range.index + range[0].length;
    const segmentEnd = ranges[index + 1]?.index ?? String(message).length;
    const segment = String(message).slice(segmentStart, segmentEnd);
    let matchedProjects = findAllMentions(projects, segment, (project) => project.name);
    if (!matchedProjects.length) {
      const previousEnd = index > 0
        ? ranges[index - 1].index + ranges[index - 1][0].length
        : 0;
      const leadingSegment = String(message).slice(previousEnd, range.index);
      matchedProjects = findAllMentions(projects, leadingSegment, (project) => project.name);
    }
    return {
      startTime: `${String(range[1]).padStart(2, "0")}:${range[2] || "00"}`,
      endTime: `${String(range[3]).padStart(2, "0")}:${range[4] || "00"}`,
      projects: matchedProjects,
    };
  });
}

function parseMonthlyWorkload(message, fallbackDate) {
  const normalizedMessage = normalize(message);
  const percentageMatch = String(message).match(/\b(\d{1,3})\s*%/)
    || normalizedMessage.match(/\b(\d{1,3})\s*procent(?:\s*(?:jobb|tjanst|sysselsattning))?\b/);
  const hoursMatch = normalizedMessage.match(/\b(\d+(?:[.,]\d+)?)\s*(?:timmar|tim|h)\b/);
  if (!percentageMatch && !hoursMatch) return null;

  const allMonths = { ...MONTHS, ...MONTH_ALIASES };
  const mentionedMonth = Object.keys(allMonths).find((monthName) =>
    new RegExp(`\\b${monthName}\\b`).test(normalizedMessage)
  );
  const refersToCurrentMonth = /\b(denna|innevarande|nuvarande)\s+manad(?:en)?\b/.test(normalizedMessage);
  if (!mentionedMonth && !refersToCurrentMonth) return null;

  const yearMatch = normalizedMessage.match(/\b(20\d{2})\b/);
  let year = Number(yearMatch?.[1]) || fallbackDate.getFullYear();
  const month = mentionedMonth ? allMonths[mentionedMonth] : fallbackDate.getMonth() + 1;
  if (!yearMatch && mentionedMonth && month < fallbackDate.getMonth() + 1) year += 1;

  if (percentageMatch) {
    const percentage = Number(percentageMatch[1]);
    if (percentage <= 0 || percentage > 100) return { error: "Sysselsättningsgraden måste vara mellan 1 % och 100 %." };
    return { year, month, percentage, requestedHours: null };
  }

  const requestedHours = Number(String(hoursMatch[1]).replace(",", "."));
  if (!Number.isFinite(requestedHours) || requestedHours <= 0) return null;
  return { year, month, percentage: null, requestedHours };
}

function parseProjectHourAllocations(message, selectedProjects) {
  const normalizedMessage = normalize(message);
  const mentions = selectedProjects.flatMap((project) => {
    const projectName = normalize(project.name);
    const index = normalizedMessage.indexOf(projectName);
    return index === -1 ? [] : [{ project, index, nameLength: projectName.length }];
  }).sort((left, right) => left.index - right.index);

  return mentions.flatMap((mention, index) => {
    const segmentEnd = mentions[index + 1]?.index ?? normalizedMessage.length;
    const segment = normalizedMessage.slice(mention.index + mention.nameLength, segmentEnd);
    const hoursMatch = segment.match(/\b(\d+(?:[.,]\d+)?)\s*(?:timmar|tim|h)\b/);
    if (!hoursMatch) return [];
    const hours = Number(hoursMatch[1].replace(",", "."));
    return Number.isFinite(hours) && hours > 0 ? [{ project: mention.project, hours }] : [];
  });
}

function createMonthlySchedule(workload, selectedProjects, projectAllocations = []) {
  const weekdays = [];
  const weekendDays = [];
  const daysInMonth = new Date(workload.year, workload.month, 0).getDate();
  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(workload.year, workload.month - 1, day);
    if (date.getDay() === 0 || date.getDay() === 6) weekendDays.push(date);
    else weekdays.push(date);
  }

  const dailyHours = workload.percentage ? 8 * (workload.percentage / 100) : 8;
  const targetHours = projectAllocations.length
    ? projectAllocations.reduce((sum, allocation) => sum + allocation.hours, 0)
    : workload.requestedHours ?? weekdays.length * dailyHours;
  const maximumHours = weekdays.length * 8;
  if (targetHours > maximumHours) {
    return { error: `Det finns högst ${maximumHours} ordinarie arbetstimmar på vardagar den månaden. Skriv ett lägre timantal.` };
  }

  const dailyCapacityMinutes = Math.round(dailyHours * 60);
  const requiredWorkdays = Math.ceil((targetHours * 60) / dailyCapacityMinutes);
  const weekendCount = Math.min(
    weekendDays.length,
    requiredWorkdays >= 8 ? 2 : requiredWorkdays >= 3 ? 1 : 0
  );
  const evenlyPick = (dates, count) => {
    if (count <= 0) return [];
    if (count >= dates.length) return dates;
    return Array.from({ length: count }, (_, index) =>
      dates[Math.floor(((index + 0.5) * dates.length) / count)]
    );
  };
  // Helgpassen ersätter vardagar och placeras på olika delar av månaden.
  // Alla pass ligger 08:00–senast 16:00, vilket ger minst 16 timmars
  // dygnsvila även när två arbetsdagar ligger direkt efter varandra.
  const workDates = [
    ...evenlyPick(weekdays, requiredWorkdays - weekendCount),
    ...evenlyPick(weekendDays, weekendCount),
  ].sort((left, right) => left - right);

  const rows = [];
  let workDateIndex = 0;
  let usedMinutesToday = 0;
  const allocations = projectAllocations.length
    ? projectAllocations
    : [{ project: null, hours: targetHours }];
  for (const allocation of allocations) {
    let projectMinutes = Math.round(allocation.hours * 60);
    while (projectMinutes > 0 && workDateIndex < workDates.length) {
      const availableMinutes = dailyCapacityMinutes - usedMinutesToday;
      const shiftMinutes = Math.min(availableMinutes, projectMinutes, Math.round(dailyHours * 60));
      const startMinutes = (8 * 60) + usedMinutesToday;
      const endMinutes = startMinutes + shiftMinutes;
      const project = allocation.project || selectedProjects[workDateIndex % selectedProjects.length];
      const date = `${workload.year}-${String(workload.month).padStart(2, "0")}-${String(workDates[workDateIndex].getDate()).padStart(2, "0")}`;
      const block = {
        startTime: `${String(Math.floor(startMinutes / 60)).padStart(2, "0")}:${String(startMinutes % 60).padStart(2, "0")}`,
        endTime: `${String(Math.floor(endMinutes / 60)).padStart(2, "0")}:${String(endMinutes % 60).padStart(2, "0")}`,
        projects: [project],
      };
      const existingRow = rows.at(-1);
      if (existingRow?.date === date) existingRow.blocks.push(block);
      else rows.push({ date, blocks: [block] });
      projectMinutes -= shiftMinutes;
      usedMinutesToday += shiftMinutes;
      if (usedMinutesToday >= dailyCapacityMinutes) {
        workDateIndex += 1;
        usedMinutesToday = 0;
      }
    }
  }
  return { rows, totalHours: targetHours, projectAllocations, weekendCount };
}

export function parseScheduleRows(message, projects, fallbackDate) {
  const allMonths = { ...MONTHS, ...MONTH_ALIASES };
  const monthNames = Object.keys(allMonths).join("|");
  const pattern = new RegExp(`\\b(\\d{1,2})(?:a|e)?\\s+(?:[a-zåäö]{1,3}\\s+)?(${monthNames})(?:\\s+(20\\d{2}))?\\b`, "gi");
  const dateMatches = [...String(message).matchAll(pattern)];
  const parsedDates = parseDates(message, fallbackDate);

  // Formuleringar som "11 och 12 augusti" delar månad, projekt och tider.
  // Datum-regexpen nedan ser då bara det sista datumet, så bygg en rad per
  // tolkat datum och återanvänd arbetsblocket från hela meddelandet.
  if (parsedDates.length > dateMatches.length) {
    const blocks = parseWorkBlocks(message, projects);
    const mentionedProjects = findAllMentions(projects, message, (project) => project.name);
    const sharedBlocks = blocks.map((block) => ({
      ...block,
      projects: block.projects.length ? block.projects : mentionedProjects,
    }));
    return parsedDates.map((date) => ({ date, blocks: sharedBlocks }));
  }

  const mentionedProjects = findAllMentions(projects, message, (project) => project.name);
  const rows = dateMatches.map((match, index) => {
    const year = Number(match[3]) || fallbackDate.getFullYear();
    const date = `${year}-${String(allMonths[normalize(match[2])]).padStart(2, "0")}-${String(Number(match[1])).padStart(2, "0")}`;
    const source = String(message);
    // När bara ett datum anges hör hela meddelandet till det datumet.
    // Då kan användare, projekt och tider stå på valfria rader och i valfri ordning.
    // Vid flera datum börjar varje efterföljande datum en ny schemasektion.
    const lineStart = source.lastIndexOf("\n", match.index) + 1;
    const nextMatchIndex = dateMatches[index + 1]?.index;
    const nextLineStart = nextMatchIndex === undefined
      ? source.length
      : source.lastIndexOf("\n", nextMatchIndex) + 1;
    const datesShareLine = index > 0 && lineStart <= dateMatches[index - 1].index;
    const sectionStart = datesShareLine ? match.index : lineStart;
    const sectionEnd = nextMatchIndex === undefined
      ? source.length
      : nextLineStart > match.index ? nextLineStart : nextMatchIndex;
    const section = dateMatches.length === 1
      ? source
      : source.slice(sectionStart, sectionEnd);
    let blocks = parseWorkBlocks(section, projects);
    const sectionProjects = findAllMentions(projects, section, (project) => project.name);
    if (blocks.length === 1 && !blocks[0].projects.length) blocks = [{ ...blocks[0], projects: sectionProjects }];
    if (mentionedProjects.length === 1) {
      blocks = blocks.map((block) => ({ ...block, projects: block.projects.length ? block.projects : mentionedProjects }));
    }
    return { date, blocks };
  });
  // Ett nytt svar på en följdfråga ersätter tidigare tider för samma datum.
  // En upprepning utan tider får däremot inte radera ett komplett pass.
  const latestRows = new Map();
  for (const row of rows) {
    if (row.blocks.length || !latestRows.has(row.date)) latestRows.set(row.date, row);
  }
  return [...latestRows.values()];
}

export function correctDraftDate(currentDraft, message, fallbackDate) {
  if (!currentDraft?.rows?.length) return null;
  const normalized = normalize(message);
  const correction = /\b(?:jag sa|jag sade|jag menar|menade|inte|datum|flytta)\b/.test(normalized);
  const dateOnly = new RegExp(`^(?:nej |den |nej den )?\\d{1,2}(?:a|e)? (?:${Object.keys(MONTHS).join("|")})(?: 20\\d{2})?$`).test(normalized);
  if (!correction && !dateOnly) return null;
  // "17 september, inte 15 september" and "inte 15 september utan 17 september".
  const contrast = normalized.match(/\binte\s+(.+?)\s+(?:utan|jag menar)\s+(.+)$/);
  const positive = contrast ? contrast[2] : normalized.split(/\binte\b/)[0];
  const negative = contrast ? contrast[1] : normalized.split(/\binte\b/)[1];
  if (!/\b\d{1,2}\b/.test(positive)) return null;
  const dates = parseDates(positive, fallbackDate);
  if (!dates.length) return null;
  if (dates.length !== 1) return { error: "Vilket datum ska passet flyttas till?" };
  const oldDates = negative ? parseDates(negative, fallbackDate) : [];
  const targets = oldDates.length ? currentDraft.rows.filter((row) => oldDates.includes(row.date)) : currentDraft.rows;
  if (targets.length !== 1) return { error: "Vilket av passen ska få det nya datumet? Ange det gamla och det nya datumet." };
  const rows = currentDraft.rows.map((row) => row === targets[0] ? { ...row, date: dates[0] } : row);
  return { draft: { ...currentDraft, rows, dates: rows.map((row) => row.date), inferredMonthOnly: false } };
}

function updateDraftFromFollowup(currentDraft, message, fallbackDate) {
  if (!currentDraft?.rows?.length) return null;

  const nextTimes = parseTimes(message);
  const normalizedMessage = normalize(
    String(message).replace(/\b(?:[01]?\d|2[0-3])[:.]\d{2}\b/g, " ")
  );
  const singleTimeMatch = String(message).match(/\b(?:starttid|start|borja|borjar|sluttid|slut|sluta|slutar|skuta|till)\D{0,12}([01]?\d|2[0-3])(?:[:.]([0-5]\d))?\b/i)
    || String(message).match(/\b([01]?\d|2[0-3])[:.]([0-5]\d)\b/);
  const singleTime = singleTimeMatch
    ? `${String(singleTimeMatch[1]).padStart(2, "0")}:${singleTimeMatch[2] || "00"}`
    : "";
  const hasCompleteRange = Boolean(nextTimes.startTime && nextTimes.endTime);
  const changesStartOnly = Boolean(!hasCompleteRange && singleTime && /\b(starttid|start|borja|borjar)\b/.test(normalizedMessage));
  const changesEndOnly = Boolean(!hasCompleteRange && singleTime && !changesStartOnly && /\b(sluttid|slut|sluta|slutar|skuta|till)\b/.test(normalizedMessage));
  if ((!nextTimes.startTime || !nextTimes.endTime) && !changesStartOnly && !changesEndOnly) return null;

  const explicitDates = referencedDraftDates(message, currentDraft, fallbackDate);
  const shortDayMatch = normalizedMessage.match(/\b(?:den|pa|dag)\s+(\d{1,2})(?:a|e)?\b/);
  const targetDates = explicitDates.length
    ? explicitDates
    : shortDayMatch
      ? currentDraft.rows
        .filter((row) => Number(row.date.slice(-2)) === Number(shortDayMatch[1]))
        .map((row) => row.date)
      : [currentDraft.rows.at(-1).date];

  if (!targetDates.length) return null;
  const targetDateSet = new Set(targetDates);
  const draftProjectList = [...new Map(
    currentDraft.rows.flatMap((row) => row.blocks)
      .flatMap((block) => block.projects)
      .map((project) => [project.id, project])
  ).values()];
  const mentionedProjects = findAllMentions(draftProjectList, message, (project) => project.name);
  const mentionedProjectIds = new Set(mentionedProjects.map((project) => project.id));
  const rows = currentDraft.rows.map((row) => targetDateSet.has(row.date)
    ? {
      ...row,
      blocks: row.blocks.map((block) => {
        const targetsBlock = !mentionedProjectIds.size
          || block.projects.some((project) => mentionedProjectIds.has(project.id));
        return targetsBlock ? {
          ...block,
          startTime: changesStartOnly ? singleTime : changesEndOnly ? block.startTime : nextTimes.startTime,
          endTime: changesEndOnly ? singleTime : changesStartOnly ? block.endTime : nextTimes.endTime,
        } : block;
      }),
    }
    : row);

  if (!rows.some((row, index) => row !== currentDraft.rows[index])) return null;
  const blocks = rows.flatMap((row) => row.blocks);
  return {
    ...currentDraft,
    rows,
    dates: rows.map((row) => row.date),
    blocks,
    startTime: blocks[0]?.startTime || "",
    endTime: blocks[0]?.endTime || "",
  };
}

function extendDraftFromFollowup(currentDraft, message, projects, fallbackDate) {
  if (!currentDraft?.rows?.length) return null;
  const normalizedMessage = normalize(message);
  const hasUpdateCommand = /\b(andra|adnra|andar|byt|flytta|korrigera|justera|uppdatera|gor om|istallet|ska vara|forkorta|forlanga)\b/.test(normalizedMessage);
  if (hasUpdateCommand) return null;
  let addedRows = parseScheduleRows(message, projects, fallbackDate);
  const hasAddCommand = /\b(lagg|lagga|lag till|lagg till|lagger till|lagg in|skapa|boka|satt in|schemalagg|registrera|skriv in|planera in|aven|ocksa|en till|kor samma)\b/.test(normalizedMessage);
  const hasCompleteAdditionalBlock = addedRows.some((row) => row.blocks.some((block) => {
    if (!block.startTime || !block.endTime || !block.projects.length) return false;
    const currentRow = currentDraft.rows.find((item) => item.date === row.date);
    if (!currentRow) return true;
    return !currentRow.blocks.some((currentBlock) =>
      currentBlock.startTime === block.startTime
      && currentBlock.endTime === block.endTime
      && currentBlock.projects.some((project) => block.projects.some((item) => item.id === project.id))
    );
  }));
  if (!hasAddCommand && !hasCompleteAdditionalBlock) return null;

  if (!addedRows.length) {
    addedRows = referencedDraftDates(message, currentDraft, fallbackDate)
      .filter((date) => !currentDraft.rows.some((row) => row.date === date))
      .map((date) => ({ date, blocks: [] }));
  }
  if (!addedRows.length) return null;

  const fallbackProjects = currentDraft.projects?.length
    ? currentDraft.projects
    : currentDraft.rows.flatMap((row) => row.blocks.flatMap((block) => block.projects));
  const referenceBlocks = currentDraft.rows.at(-1)?.blocks || [];
  const usesReference = /\b(samma|aven|ocksa|den|det|detta|honom|henne)\b/.test(normalizedMessage);
  const usesSameTime = usesReference || /\bsamma\b.*\btid(?:en)?\b|\btid(?:en)?\b.*\bsamma\b/.test(normalizedMessage);
  const usesSameProject = /\bsamma\b.*\bprojekt(?:et)?\b|\bprojekt(?:et)?\b.*\bsamma\b/.test(normalizedMessage);
  const mentionedProjects = findAllMentions(projects, message, (project) => project.name);
  const completedRows = addedRows.map((row) => {
    const sourceBlocks = row.blocks.length
      ? row.blocks
      : usesSameTime
        ? referenceBlocks.map((block) => ({
          ...block,
          projects: usesSameProject
            ? block.projects
            : mentionedProjects.length ? mentionedProjects : block.projects,
        }))
        : [];
    return {
      ...row,
      blocks: sourceBlocks.map((block) => ({
      ...block,
      projects: block.projects.length ? block.projects : fallbackProjects,
      })),
    };
  });
  if (completedRows.some((row) => row.blocks.some((block) => !block.projects.length))) return null;
  if (completedRows.some((row) => !row.blocks.length)) return null;

  const rows = [...currentDraft.rows];
  completedRows.forEach((addedRow) => {
    const existingIndex = rows.findIndex((row) => row.date === addedRow.date);
    if (existingIndex === -1) rows.push(addedRow);
    else rows[existingIndex] = {
      ...rows[existingIndex],
      blocks: [...rows[existingIndex].blocks, ...addedRow.blocks],
    };
  });
  rows.sort((left, right) => left.date.localeCompare(right.date));

  const blocks = rows.flatMap((row) => row.blocks);
  const draftProjects = [...new Map(
    blocks.flatMap((block) => block.projects).map((project) => [project.id, project])
  ).values()];
  return {
    ...currentDraft,
    projects: draftProjects,
    rows,
    dates: rows.map((row) => row.date),
    blocks,
    startTime: blocks[0]?.startTime || "",
    endTime: blocks[0]?.endTime || "",
  };
}

function removeFromDraftFollowup(currentDraft, message, fallbackDate) {
  if (!currentDraft?.rows?.length) return null;
  const normalizedMessage = normalize(message);
  if (!/\b(ta bort|ta bot|ta bor|radera|delete|stryk|bort med|avboka|plocka bort)\b/.test(normalizedMessage)) return null;

  let explicitDates = referencedDraftDates(message, currentDraft, fallbackDate);
  if (!explicitDates.length) {
    const withoutTimes = normalize(String(message).replace(/\b(?:[01]?\d|2[0-3])[:.]\d{2}\b/g, " "));
    const mentionedDays = [...withoutTimes.matchAll(/\b([1-9]|[12]\d|3[01])(?:a|e)?\b/g)]
      .map((match) => Number(match[1]));
    const referenceDate = currentDraft.rows.at(-1)?.date;
    if (referenceDate) {
      const [year, month] = referenceDate.split("-").map(Number);
      explicitDates = [...new Set(mentionedDays)].map((day) =>
        `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
      );
    }
  }
  const shortDayMatch = normalizedMessage.match(/\b(?:den|dag|ta bort|ta bot|radera|plocka bort)\s+(\d{1,2})(?:a|e)?\b/);
  const targetDates = explicitDates.length
    ? explicitDates
    : shortDayMatch
      ? currentDraft.rows
        .filter((row) => Number(row.date.slice(-2)) === Number(shortDayMatch[1]))
        .map((row) => row.date)
      : [];
  if (!targetDates.length) return null;

  const targetDateSet = new Set(targetDates);
  const draftProjectList = [...new Map(
    currentDraft.rows.flatMap((row) => row.blocks)
      .flatMap((block) => block.projects)
      .map((project) => [project.id, project])
  ).values()];
  const mentionedProjects = findAllMentions(draftProjectList, message, (project) => project.name);
  const mentionedProjectIds = new Set(mentionedProjects.map((project) => project.id));
  let removedBlockCount = 0;
  const rows = currentDraft.rows.flatMap((row) => {
    if (!targetDateSet.has(row.date)) return [row];
    if (!mentionedProjectIds.size) {
      removedBlockCount += row.blocks.length;
      return [];
    }
    const blocks = row.blocks.filter((block) => {
      const removesBlock = block.projects.some((project) => mentionedProjectIds.has(project.id));
      if (removesBlock) removedBlockCount += 1;
      return !removesBlock;
    });
    return blocks.length ? [{ ...row, blocks }] : [];
  });
  if (!removedBlockCount || !rows.length) return null;

  const blocks = rows.flatMap((row) => row.blocks);
  const draftProjects = [...new Map(
    blocks.flatMap((block) => block.projects).map((project) => [project.id, project])
  ).values()];
  return {
    ...currentDraft,
    projects: draftProjects,
    rows,
    dates: rows.map((row) => row.date),
    blocks,
    startTime: blocks[0]?.startTime || "",
    endTime: blocks[0]?.endTime || "",
  };
}

function restoreDraftFollowup(currentDraft, previousDraft, message, fallbackDate) {
  if (!currentDraft?.rows?.length || !previousDraft?.rows?.length) return null;
  const normalizedMessage = normalize(message);
  if (!/\b(lagg tillbaka|aterstall|angra borttagning)\b/.test(normalizedMessage)) return null;

  const currentDates = new Set(currentDraft.rows.map((row) => row.date));
  let removedRows = previousDraft.rows.filter((row) => !currentDates.has(row.date));
  const currentEmployeeIds = new Set(currentDraft.employees.map((employee) => employee.id));
  let removedEmployees = previousDraft.employees.filter((employee) => !currentEmployeeIds.has(employee.id));
  const namedRemovedEmployees = removedEmployees.filter((employee) => {
    const fullName = normalize(employee.name);
    const nameWords = fullName.split(" ").filter((word) => word.length > 2);
    return ` ${normalizedMessage} `.includes(` ${fullName} `)
      || nameWords.some((word) => new RegExp(`\\b${word}\\b`).test(normalizedMessage));
  });
  if (namedRemovedEmployees.length) removedEmployees = namedRemovedEmployees;
  const explicitDates = parseDates(message, fallbackDate);
  const shortDayMatch = normalizedMessage.match(/\b(?:den|dag)\s+(\d{1,2})(?:a|e)?\b/);
  if (explicitDates.length) {
    const targetDateSet = new Set(explicitDates);
    removedRows = removedRows.filter((row) => targetDateSet.has(row.date));
  } else if (shortDayMatch) {
    removedRows = removedRows.filter((row) => Number(row.date.slice(-2)) === Number(shortDayMatch[1]));
  }
  if (!removedRows.length && !removedEmployees.length) return null;

  const rows = [...currentDraft.rows, ...removedRows]
    .sort((left, right) => left.date.localeCompare(right.date));
  const restoredEmployees = [...new Map(
    [...currentDraft.employees, ...removedEmployees].map((employee) => [employee.id, employee])
  ).values()];
  const blocks = rows.flatMap((row) => row.blocks);
  const draftProjects = [...new Map(
    blocks.flatMap((block) => block.projects).map((project) => [project.id, project])
  ).values()];
  return {
    ...currentDraft,
    employees: restoredEmployees,
    projects: draftProjects,
    rows,
    dates: rows.map((row) => row.date),
    blocks,
    startTime: blocks[0]?.startTime || "",
    endTime: blocks[0]?.endTime || "",
  };
}

function updateDraftEmployeesFollowup(currentDraft, message, employees) {
  if (!currentDraft?.employees?.length) return null;
  const normalizedMessage = normalize(message);
  const mentionedEmployees = findAllMentions(employees, message, (employee) => employee.name);
  if (!mentionedEmployees.length) return null;

  const removesEmployee = /\b(ta bort|ta bot|ta bor|radera|stryk|plocka bort)\b/.test(normalizedMessage);
  const addsEmployee = /\b(lagg|lagga|lagg till|lagg in|aven|ocksa|samma pass|fler anvandare|fler personer)\b/.test(normalizedMessage);
  if (!removesEmployee && !addsEmployee) return null;

  if (removesEmployee) {
    const removedIds = new Set(mentionedEmployees.map((employee) => employee.id));
    const nextEmployees = currentDraft.employees.filter((employee) => !removedIds.has(employee.id));
    if (!nextEmployees.length || nextEmployees.length === currentDraft.employees.length) return null;
    return { ...currentDraft, employees: nextEmployees };
  }

  const nextEmployees = [...new Map(
    [...currentDraft.employees, ...mentionedEmployees].map((employee) => [employee.id, employee])
  ).values()];
  if (nextEmployees.length === currentDraft.employees.length) return null;
  return { ...currentDraft, employees: nextEmployees };
}

export function isUndoScheduleChange(message) {
  return /^(?:(?:nej|kan du|snalla) )*(?:andra tillbaka|byt tillbaka|angra|angra (?:den )?(?:senaste )?andringen|aterstall (?:det tidigare|forra) schemat)(?: tack)?$/.test(normalize(message));
}

export function savedVoiceState(previous, draft, records, { followup = false, undo = false } = {}) {
  const history = previous?.history || [];
  return {
    draft,
    records,
    history: undo ? history.slice(0, -1) : followup && previous ? [...history, previous.draft] : [],
  };
}

export function employeesForScheduleRow(draft, row) {
  return draft.employees.filter((employee) => employee.id && (!row.employeeIds || row.employeeIds.includes(employee.id)));
}

function ScheduleChatBubble({ draft, employees, projects, saved, isSaving, onChange, onApprove, onUndo }) {
  const [isEditing, setIsEditing] = useState(false);
  const employeeOptions = [UNSTAFFED_EMPLOYEE, ...employees];
  const timeToMinutes = (time) => {
    const [hours, minutes] = String(time || "").split(":").map(Number);
    return (hours * 60) + minutes;
  };
  const hasTimeConflict = (rowIndex, blockIndex) => {
    const row = draft.rows[rowIndex];
    const block = row.blocks[blockIndex];
    const start = timeToMinutes(block.startTime);
    const end = timeToMinutes(block.endTime);
    return draft.rows.some((otherRow, otherRowIndex) =>
      otherRow.date === row.date && employeesForScheduleRow(draft, row).some((employee) => employeesForScheduleRow(draft, otherRow).some((other) => other.id === employee.id)) && otherRow.blocks.some((otherBlock, otherBlockIndex) => {
        if (otherRowIndex === rowIndex && otherBlockIndex === blockIndex) return false;
        const otherStart = timeToMinutes(otherBlock.startTime);
        const otherEnd = timeToMinutes(otherBlock.endTime);
        // Exempelvis 10:00-20:00 och 20:00-23:00 får mötas i
        // gränsen 20:00 utan att räknas som en tidskrock.
        return start < otherEnd && otherStart < end;
      })
    );
  };
  const commitRows = (rows) => {
    const blocks = rows.flatMap((row) => row.blocks);
    const nextProjects = [...new Map(
      blocks.flatMap((block) => block.projects).map((project) => [project.id, project])
    ).values()];
    onChange({
      ...draft,
      projects: nextProjects,
      rows,
      dates: rows.map((row) => row.date),
      blocks,
      startTime: blocks[0]?.startTime || "",
      endTime: blocks[0]?.endTime || "",
    });
  };
  const updateRow = (rowIndex, updater) => {
    commitRows(draft.rows.map((row, index) => index === rowIndex ? updater(row) : row));
  };
  const removeBlock = (rowIndex, blockIndex) => {
    if (draft.rows.reduce((total, row) => total + row.blocks.length, 0) <= 1) return;
    const rows = draft.rows.flatMap((row, index) => {
      if (index !== rowIndex) return [row];
      const blocks = row.blocks.filter((_, currentBlockIndex) => currentBlockIndex !== blockIndex);
      return blocks.length ? [{ ...row, blocks }] : [];
    });
    commitRows(rows);
  };
  const updateEmployee = (employeeIndex, employeeId) => {
    const selectedEmployee = employeeOptions.find((employee) => employee.id === employeeId);
    if (!selectedEmployee) return;
    onChange({
      ...draft,
      employees: draft.employees.map((employee, index) =>
        index === employeeIndex ? selectedEmployee : employee
      ),
    });
  };
  const addEmployee = () => {
    onChange({ ...draft, employees: [...draft.employees, { id: "", name: "" }] });
  };
  const removeEmployee = (employeeIndex) => {
    if (draft.employees.length <= 1) return;
    onChange({
      ...draft,
      employees: draft.employees.filter((_, index) => index !== employeeIndex),
    });
  };
  return <section className="chat-response" aria-label="Schemaförslag">
    <div className="assistant-response"><div className="assistant-avatar"><FaMagic /></div><div className="assistant-message">
      {isEditing ? <div className="inline-schedule-editor">
        {!draft.rows.some((row) => row.employeeIds) && <div className="inline-employees-editor">
          <div className="inline-editor-heading"><span>Anställda</span><button type="button" className="add-edit-employee" onClick={addEmployee} aria-label="Lägg till anställd" title="Lägg till anställd">+</button></div>
          {draft.employees.map((draftEmployee, employeeIndex) => <div className="inline-employee-row" key={`${draftEmployee.id}-${employeeIndex}`}>
            <select aria-label={`Anställd ${employeeIndex + 1}`} value={draftEmployee.id || ""} onChange={(event) => updateEmployee(employeeIndex, event.target.value)}>
              <option value="" disabled>Välj anställd…</option>
              {employeeOptions.filter((employee) => employee.id === draftEmployee.id || !draft.employees.some((selected) => selected.id === employee.id)).map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
            </select>
            <button type="button" className="remove-edit-employee" onClick={() => removeEmployee(employeeIndex)} disabled={draft.employees.length <= 1} aria-label={`Ta bort ${draftEmployee.name}`} title="Ta bort anställd">−</button>
          </div>)}
        </div>}
        {draft.rows.map((row, rowIndex) => <div className="inline-edit-row" key={`${row.date}-${rowIndex}`}>
          {row.employeeIds && <strong>{employeesForScheduleRow(draft, row).map((employee) => employee.name).join(", ")}</strong>}
          <label>Datum<input type="date" value={row.date} onChange={(event) => updateRow(rowIndex, (current) => ({ ...current, date: event.target.value }))} /></label>
          {row.blocks.map((block, blockIndex) => <div className="inline-edit-block" key={`${rowIndex}-${blockIndex}`}>
            <button className="delete-edit-block" type="button" onClick={() => removeBlock(rowIndex, blockIndex)} disabled={draft.rows.reduce((total, currentRow) => total + currentRow.blocks.length, 0) <= 1} aria-label={`Ta bort passet ${row.date}`} title="Ta bort pass"><FaTrash /></button>
            <label>Start<input type="time" value={block.startTime} onChange={(event) => updateRow(rowIndex, (current) => ({ ...current, blocks: current.blocks.map((item, index) => index === blockIndex ? { ...item, startTime: event.target.value } : item) }))} /></label>
            <label>Slut<input type="time" value={block.endTime} onChange={(event) => updateRow(rowIndex, (current) => ({ ...current, blocks: current.blocks.map((item, index) => index === blockIndex ? { ...item, endTime: event.target.value } : item) }))} /></label>
            <label className="inline-project-field">Projekt<select value={block.projects[0]?.id || ""} onChange={(event) => { const selectedProject = projects.find((project) => project.id === event.target.value); updateRow(rowIndex, (current) => ({ ...current, blocks: current.blocks.map((item, index) => index === blockIndex ? { ...item, projects: selectedProject ? [selectedProject] : [] } : item) })); }}>{projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}</select></label>
          </div>)}
        </div>)}
      </div> : <>
        <div className="schedule-employees">{draft.employees.filter((employee) => employee.id).map((employee) => <strong key={employee.id}>{employee.name}</strong>)}</div>
        {draft.monthlyWorkload && <span className="schedule-pass-line">Månadsschema: {draft.monthlyWorkload.percentage ? `${draft.monthlyWorkload.percentage} %` : `${draft.monthlyWorkload.totalHours} timmar`} · totalt {draft.monthlyWorkload.totalHours} timmar · {draft.monthlyWorkload.weekendCount} utspridda helgpass · minst 11 h dygnsvila{draft.monthlyWorkload.projectAllocations?.length ? ` · ${draft.monthlyWorkload.projectAllocations.map((allocation) => `${allocation.project.name}: ${allocation.hours} h`).join(" · ")}` : ""}</span>}
        {draft.copyWarnings?.map((warning, index) => <div className="copy-date-warning" role="alert" key={`${warning.sourceDate}-${index}`}><FaQuestionCircle /><span><strong>{warning.sourceLabel} kunde inte kopieras.</strong> {warning.targetMonthName} har bara {warning.daysInTargetMonth} dagar. Passet {warning.startTime}-{warning.endTime} på {warning.project} saknas ({warning.hours} timmar).</span></div>)}
        {draft.rows.flatMap((row, rowIndex) => row.blocks.flatMap((block, blockIndex) => block.projects.map((project) => <span className="schedule-pass-line" key={`${row.date}-${rowIndex}-${blockIndex}-${project.id}`}>{row.employeeIds && `${employeesForScheduleRow(draft, row).map((employee) => employee.name).join(", ")} · `}{project.name} {new Date(`${row.date}T12:00:00`).toLocaleDateString("sv-SE", { day: "numeric", month: "long" })} {block.startTime}-{block.endTime}{hasTimeConflict(rowIndex, blockIndex) && <FaQuestionCircle className="schedule-conflict-icon" title="Passet krockar tidsmässigt med ett annat pass samma dag" aria-label="Tidskrock" />}</span>)))}
      </>}
      <div className="schedule-chat-actions">
        <button className="edit-chat" type="button" onClick={() => setIsEditing((value) => !value)} disabled={saved}>{isEditing ? "Klar" : "Ändra"}</button>
        <button className="undo-chat" type="button" onClick={onUndo} disabled={!saved || isSaving} aria-label="Återkalla tillagda pass" title="Återkalla tillagda pass"><FaRedoAlt /></button>
        <button className={`approve-chat ${saved ? "is-added" : ""}`} onClick={onApprove} disabled={isSaving || saved}><FaCheck /> {saved ? "Tillagd!" : isSaving ? "Lägger till…" : "Lägg till i schemat"}</button>
      </div>
    </div></div>
  </section>;
}

export default function CreateAutomaticShiftPopup({
  isOpen,
  onClose,
  companyRootUid,
  selectedMonth,
  selectedSchema,
  initialEmployeeId,
  onSelectTemplate,
  selectedTemplate,
}) {
  const { currentUser } = useAuth();
  const { activeCompany } = useCompany();
  const companyUid = companyRootUid || activeCompany?.companyAccountUid;
  const [employedEmployees, setEmployedEmployees] = useState([]);
  const [ownerEmployee, setOwnerEmployee] = useState(null);
  const [schemaMembership, setSchemaMembership] = useState({ schemaId: null, employeeIds: null });
  const selectedEmployeeIds = schemaMembership.schemaId === selectedSchema ? schemaMembership.employeeIds : null;
  const employees = useMemo(() => filterEmployeesForSchema(
    mergeSchedulingEmployees(employedEmployees, ownerEmployee), selectedEmployeeIds
  ), [employedEmployees, ownerEmployee, selectedEmployeeIds]);
  const [allProjects, setProjects] = useState([]);
  const projects = useVisibleProjects(allProjects);
  const [message, setMessage] = useState("");
  const [sentMessage, setSentMessage] = useState("");
  const [assistantReply, setAssistantReply] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedShiftIds, setSavedShiftIds] = useState([]);
  const [panelWidth, setPanelWidth] = useState(420);
  const [isResizing, setIsResizing] = useState(false);
  const [showScheduleBuilder, setShowScheduleBuilder] = useState(false);
  const [showScheduleFetcher, setShowScheduleFetcher] = useState(false);
  const [fetchEmployeeIds, setFetchEmployeeIds] = useState([]);
  const [fetchEmployeeSearch, setFetchEmployeeSearch] = useState("");
  const matchingFetchEmployees = employees.filter((employee) => normalize(employee.name).includes(normalize(fetchEmployeeSearch)));
  const [fetchMonth, setFetchMonth] = useState(new Date().getMonth() + 1);
  const [fetchYear, setFetchYear] = useState(new Date().getFullYear());
  const [isFetchingSchedule, setIsFetchingSchedule] = useState(false);
  const [fetchedSchedule, setFetchedSchedule] = useState(null);
  const [activeFetchedEmployeeId, setActiveFetchedEmployeeId] = useState("");
  const [copyScheduleMonth, setCopyScheduleMonth] = useState(new Date().getMonth() + 1);
  const [copyScheduleYear, setCopyScheduleYear] = useState(new Date().getFullYear());
  const messageRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const thinkingTimeoutRef = useRef(null);
  const saveInFlightRef = useRef(false);
  const submitVoiceRef = useRef(null);
  const voiceSavedRef = useRef(null);
  const scopeRef = useRef("");
  const allowedEmployeesRef = useRef({ schemaId: null, employeeIds: null, employees: [] });
  allowedEmployeesRef.current = { schemaId: schemaMembership.schemaId, employeeIds: selectedEmployeeIds, employees };
  scopeRef.current = `${companyUid}:${selectedSchema}:${currentUser?.uid}:${isOpen}`;
  const voiceBusyRef = useRef(false);
  const lastSpokenRef = useRef("");
  const { active: voiceActive, paused: voicePaused, speaking: isSpeaking, listening: isListening, stop: stopVoice, speak: speakReply, toggle: toggleVoiceInput } = useVoiceConversation({
    isOpen, scopeKey: `${companyUid}:${selectedSchema}:${currentUser?.uid}`,
    onText: setMessage, onSubmit: (text) => submitVoiceRef.current?.(text), onError: setError,
  });
  useEffect(() => {
    if (!isOpen) return;
    const reply = error || assistantReply;
    if (reply && reply !== lastSpokenRef.current) {
      lastSpokenRef.current = reply;
      speakReply(reply);
    }
  }, [assistantReply, error, isOpen, speakReply]);
  useEffect(() => { voiceSavedRef.current = null; lastSpokenRef.current = ""; }, [companyUid, selectedSchema, currentUser?.uid]);
  // Behåll ett ofullständigt schema mellan chattmeddelanden. Om användaren
  // exempelvis först anger person, datum och tid och därefter bara svarar med
  // projektets namn ska båda meddelandena tolkas som ett gemensamt underlag.
  const pendingScheduleInputRef = useRef("");

  useEffect(() => {
    if (!isOpen || !companyUid) return undefined;
    setEmployedEmployees([]);
    setOwnerEmployee(null);
    const unsubEmployees = onSnapshot(collection(db, "users", companyUid, "employed"), (snapshot) => {
      setEmployedEmployees(snapshot.docs.map((item) => ({ id: item.id, ...item.data(), name: employeeName(item.data()) })));
    });
    const unsubOwner = onSnapshot(doc(db, "users", companyUid), (snapshot) => {
      setOwnerEmployee(readSchedulingOwner(snapshot));
    });
    const unsubProjects = onSnapshot(collection(db, "users", companyUid, "projects"), (snapshot) => {
      const projectList = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data(), name: String(item.data().name || "").trim() }))
        .filter((project) => project.name)
        .sort((left, right) => left.name.localeCompare(right.name, "sv-SE"));
      setProjects(projectList);
    });
    return () => { unsubEmployees(); unsubOwner(); unsubProjects(); };
  }, [isOpen, companyUid]);

  useEffect(() => {
    setSchemaMembership({ schemaId: null, employeeIds: null });
    if (!isOpen || !companyUid || !selectedSchema) return undefined;
    return onSnapshot(doc(db, "users", companyUid, "Scheduling", selectedSchema), (snapshot) => {
      const ids = snapshot.exists() ? snapshot.data().visibleEmployeeIds : null;
      setSchemaMembership({ schemaId: selectedSchema, employeeIds: Array.isArray(ids) ? ids : [] });
    }, (snapshotError) => {
      console.error("Kunde inte hämta schemats användare:", snapshotError);
      setSchemaMembership({ schemaId: selectedSchema, employeeIds: null });
      setError("Kunde inte hämta schemats användare. Försök igen.");
    });
  }, [isOpen, companyUid, selectedSchema]);

  useEffect(() => {
    if (fetchEmployeeIds.some((id) => !employees.some((employee) => employee.id === id))) {
      setFetchEmployeeIds((ids) => ids.filter((id) => employees.some((employee) => employee.id === id)));
      setFetchedSchedule(null);
    }
    if (selectedEmployee && !employees.some((employee) => employee.id === selectedEmployee.id)) {
      setSelectedEmployee(null);
    }
  }, [employees, fetchEmployeeIds, selectedEmployee]);

  // Konversationen behålls när panelen stängs och öppnas igen.
  // Rensa vid byte av företag eller schema så att gamla personer inte följer med.
  useEffect(() => {
    clearTimeout(thinkingTimeoutRef.current);
    setMessage(""); setSentMessage(""); setAssistantReply(""); setChatHistory([]); setSelectedEmployee(null); setDraft(null); setError(""); setSaved(false); setSavedShiftIds([]); setIsThinking(false); setPanelWidth(420); setIsResizing(false); setShowScheduleFetcher(false); setShowScheduleBuilder(false); setFetchEmployeeIds([]); setFetchEmployeeSearch(""); setFetchMonth(new Date().getMonth() + 1); setFetchYear(new Date().getFullYear()); setFetchedSchedule(null); setCopyScheduleMonth(new Date().getMonth() + 1); setCopyScheduleYear(new Date().getFullYear()); pendingScheduleInputRef.current = "";
  }, [companyUid, selectedSchema]);

  useEffect(() => () => {
    clearTimeout(thinkingTimeoutRef.current);
  }, []);

  useEffect(() => {
    const chat = chatMessagesRef.current;
    if (chat) chat.scrollTo({ top: chat.scrollHeight, behavior: "smooth" });
  }, [activeFetchedEmployeeId, assistantReply, chatHistory, draft, error, fetchedSchedule, isOpen, isThinking, saved, sentMessage, showScheduleBuilder]);

  useEffect(() => {
    if (!isResizing) return undefined;
    const handlePointerMove = (event) => {
      const maxWidth = Math.min(900, window.innerWidth - 24);
      const nextWidth = Math.max(400, Math.min(maxWidth, window.innerWidth - event.clientX));
      setPanelWidth(Math.abs(nextWidth - 420) <= 24 ? 420 : nextWidth);
    };
    const stopResizing = () => setIsResizing(false);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopResizing);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "ew-resize";
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stopResizing);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isResizing]);

  const employeeSuggestions = useMemo(() => {
    if (!message.trim()) return [];
    const employeeChoices = [UNSTAFFED_EMPLOYEE, ...employees];
    const alreadyMatched = findAllMentions(employeeChoices, message, (item) => item.name);
    const normalizedMessage = normalize(message);
    if (alreadyMatched.some((item) => normalizedMessage.endsWith(normalize(item.name)))) return [];
    const query = normalizedMessage.split(" ").filter(Boolean).pop() || "";
    if (query.length < 2 || /^\d+$/.test(query)) return [];
    return employeeChoices.filter((employee) => {
      if (alreadyMatched.some((item) => item.id === employee.id)) return false;
      return normalize(employee.name).split(" ").some((nameWord) => nameWord.startsWith(query));
    }).slice(0, 4);
  }, [employees, message]);

  const projectSuggestions = useMemo(() => {
    if (!message.trim()) return [];
    const alreadyMatched = findAllMentions(projects, message, (item) => item.name);
    const normalizedMessage = normalize(message);
    if (alreadyMatched.some((item) => normalizedMessage.endsWith(normalize(item.name)))) return [];
    const input = messageRef.current;
    const caret = input?.selectionStart ?? message.length;
    const wordAtCaret = message.slice(0, caret).match(/[\p{L}\p{N}]+$/u)?.[0] || "";
    const query = normalize(wordAtCaret);
    if (query.length < 2 || /^\d+$/.test(query)) return [];
    return projects.filter((project) => {
      if (alreadyMatched.some((item) => item.id === project.id)) return false;
      const nameWords = normalize(project.name).split(" ");
      return nameWords.some((nameWord) => nameWord.startsWith(query));
    }).slice(0, 4);
  }, [message, projects]);

  const timeSuggestion = useMemo(() => {
    const input = messageRef.current;
    const caret = input?.selectionStart ?? message.length;
    const beforeCaret = message.slice(0, caret);
    const match = beforeCaret.match(/(?:^|\s)(\d{1,2})$/);
    if (!match) return null;
    const hour = Number(match[1]);
    if (hour < 0 || hour > 23) return null;
    return `${String(hour).padStart(2, "0")}:00`;
  }, [message]);

  const monthSuggestions = useMemo(() => {
    const input = messageRef.current;
    const caret = input?.selectionStart ?? message.length;
    const beforeCaret = message.slice(0, caret);
    const wordMatch = beforeCaret.match(/[\p{L}]+$/u);
    if (!wordMatch) return [];
    const query = normalize(wordMatch[0]);
    if (query.length < 2 || MONTHS[query]) return [];
    return Object.keys(MONTHS).filter((month) => month.startsWith(query)).slice(0, 3);
  }, [message]);

  const insertSuggestion = (value) => {
    const input = messageRef.current;
    const caret = input?.selectionStart ?? message.length;
    const normalizedValue = normalize(value);
    const valueWords = normalizedValue.split(" ").filter(Boolean);
    const typedWords = [...message.matchAll(/[\p{L}\p{N}]+/gu)];
    const matchingWord = typedWords
      .filter((match) => {
        const typed = normalize(match[0]);
        const wordEnd = match.index + match[0].length;
        return wordEnd <= caret && typed.length >= 2 && valueWords.some((word) => word.startsWith(typed));
      })
      .sort((a, b) => {
        const distanceA = caret - (a.index + a[0].length);
        const distanceB = caret - (b.index + b[0].length);
        return distanceA - distanceB || b.index - a.index;
      })[0];

    let nextMessage;
    let nextCaret;
    if (matchingWord && !normalize(message).includes(normalizedValue)) {
      const start = matchingWord.index;
      const end = start + matchingWord[0].length;
      nextMessage = `${message.slice(0, start)}${value}${message.slice(end)}`;
      nextCaret = start + value.length;
    } else {
      const start = caret;
      const end = input?.selectionEnd ?? message.length;
      const before = message.slice(0, start);
      const after = message.slice(end);
      const leadingSpace = before && !/\s$/.test(before) ? " " : "";
      const trailingSpace = after && !/^\s/.test(after) ? " " : "";
      nextMessage = `${before}${leadingSpace}${value}${trailingSpace}${after}`;
      nextCaret = before.length + leadingSpace.length + value.length + trailingSpace.length;
    }
    setMessage(nextMessage);
    setError("");
    requestAnimationFrame(() => {
      messageRef.current?.focus();
      messageRef.current?.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const insertTimeSuggestion = (value) => {
    const input = messageRef.current;
    const caret = input?.selectionStart ?? message.length;
    const beforeCaret = message.slice(0, caret);
    const numberMatch = beforeCaret.match(/\d{1,2}$/);
    if (!numberMatch) return;
    const start = caret - numberMatch[0].length;
    const after = message.slice(caret);
    const needsSpace = !after || !/^\s/.test(after);
    const nextMessage = `${message.slice(0, start)}${value}${needsSpace ? " " : ""}${after}`;
    const nextCaret = start + value.length + (needsSpace ? 1 : 0);
    setMessage(nextMessage);
    setError("");
    requestAnimationFrame(() => {
      messageRef.current?.focus();
      messageRef.current?.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const archiveCurrentExchange = () => {
    if (!sentMessage) return;
    setChatHistory((history) => [
      ...history,
      { type: "user", text: sentMessage },
      ...(draft
        ? [{ type: "schedule", draft, saved, savedShiftIds }]
        : assistantReply
          ? [{ type: "assistant", text: assistantReply }]
          : []),
    ]);
  };

  const interpretMessage = async (voiceText) => {
    const fromVoice = typeof voiceText === "string";
    const inputScope = scopeRef.current;
    if (voiceBusyRef.current || saveInFlightRef.current) {
      setMessage(typeof voiceText === "string" ? voiceText : message);
      setError("Sparar fortfarande. Ditt nästa kommando finns i skrivfältet.");
      return;
    }
    lastSpokenRef.current = "";
    let priorVoiceSave = voiceSavedRef.current;
    const acceptDraft = async (next, followup = false, undo = false) => {
      setDraft(next); setIsThinking(false);
      if (!fromVoice && !(followup && priorVoiceSave)) return;
      if (next.inferredMonthOnly) { setAssistantReply("Vilket datum i månaden menar du?"); return; }
      voiceBusyRef.current = true;
      try {
        const result = await approveShift(next, null, followup ? priorVoiceSave?.records : null);
        if (result && inputScope === scopeRef.current) {
          voiceSavedRef.current = savedVoiceState(priorVoiceSave, next, result, { followup, undo });
          if (undo) setAssistantReply("Den senaste ändringen är återställd.");
          speakReply(undo ? "Den senaste ändringen är återställd." : followup ? "Ändrat." : "Inlagt.");
        } else if (undo && inputScope === scopeRef.current && priorVoiceSave) {
          setDraft(priorVoiceSave.draft); setSaved(true);
          setSavedShiftIds(priorVoiceSave.records.map((record) => record.id));
        }
      } finally { voiceBusyRef.current = false; }
    };
    const inputMessage = typeof voiceText === "string" ? voiceText : message;
    if (/^(?:godkänn|spara|spara schemat|lägg till i schemat)[.!?]*$/i.test(inputMessage.trim()) && draft) {
      if (saved || saveInFlightRef.current) return;
      approveShift(draft);
      return;
    }
    setError(""); setSaved(false);
    if (!inputMessage.trim()) { setError("Skriv först hur passet ska se ut."); return; }
    if (voiceActive && /^(avsluta|avsluta samtalet)$/i.test(inputMessage.trim())) { stopVoice(); return; }
    archiveCurrentExchange();
    setSavedShiftIds([]);
    const submittedMessage = inputMessage.trim();
    const normalizedMessage = normalize(submittedMessage);
    if (isUndoScheduleChange(submittedMessage)) {
      clearTimeout(thinkingTimeoutRef.current);
      setIsThinking(false); setSentMessage(submittedMessage); setMessage("");
      pendingScheduleInputRef.current = "";
      const previous = priorVoiceSave?.history?.at(-1);
      if (!previous) {
        if (priorVoiceSave) {
          setDraft(priorVoiceSave.draft); setSaved(true);
          setSavedShiftIds(priorVoiceSave.records.map((record) => record.id));
        }
        setAssistantReply("Det finns ingen tidigare sparad ändring i samtalet att återställa.");
        return;
      }
      setAssistantReply("");
      await acceptDraft(previous, true, true);
      return;
    }
    const conversationalReply = (() => {
      if (/\b(vem ar du|vem skriver jag med|vem pratar jag med|vad heter du)\b/.test(normalizedMessage)) {
        const greeting = /^(hej|hejsan|tjena|halla|god morgon|god kvall)\b/.test(normalizedMessage)
          ? "Hej! "
          : "";
        return `${greeting}Jag heter Lunero! Behöver du hjälp med att lägga ett schema? Skriv vem som ska jobba, på vilket projekt, vilket datum och vilka tider, så hjälper jag dig.`;
      }
      if (/^(hej|hejsan|tjena|halla|god morgon|god kvall)\b/.test(normalizedMessage)) {
        return "Hej! Jag heter Lunero!";
      }
      if (/^(tack|tusen tack|toppen|perfekt|bra)\b/.test(normalizedMessage)) {
        return "Varsågod! Skriv bara nästa schema du vill lägga in så hjälper jag dig.";
      }
      if (/^(vanta|vanta lite|avvakta|stopp)\b/.test(normalizedMessage)) {
        return "Okej, jag väntar. Skriv när du vill fortsätta.";
      }
      if (/\b(hjälp|hur gör jag|vad kan du|vad ska jag skriva)\b/.test(normalizedMessage)) {
        return "Du kan till exempel skriva: Daniel Florentz ska jobba på Hugo Finspång den 11 och 12 augusti kl. 10:00 till 20:00.";
      }
      return "";
    })();

    if (conversationalReply) {
      clearTimeout(thinkingTimeoutRef.current);
      setSentMessage(submittedMessage);
      setMessage("");
      setDraft(null);
      setAssistantReply("");
      setIsThinking(true);
      thinkingTimeoutRef.current = setTimeout(() => {
        setAssistantReply(conversationalReply);
        setIsThinking(false);
      }, typeof voiceText === "string" ? 0 : 700);
      return;
    }
    let switchingEmployee = false;
    if (fromVoice && priorVoiceSave) {
      let mentioned = findClosestEmployees(employees, submittedMessage);
      const exact = employees.filter((employee) => ` ${normalizedMessage} `.includes(` ${normalize(employee.name)} `));
      if (!mentioned.length) mentioned = employees.filter((employee) => {
        const firstName = normalize(employee.name).split(" ")[0];
        return firstName.length >= 3 && ` ${normalizedMessage} `.includes(` ${firstName} `);
      });
      const editingStaff = /\b(?:ta bort|radera|stryk|byt ut|ersatt)\b/.test(normalizedMessage);
      if (!editingStaff && mentioned.some((employee) => !priorVoiceSave.draft.employees.some((old) => old.id === employee.id))) {
        if (!exact.length && mentioned.length > 1) {
          setAssistantReply(`Vem menar du: ${mentioned.map((employee) => employee.name).join(" eller ")}? Säg hela namnet och kommandot igen.`);
          return;
        }
        if (/\bsamma pass\b/.test(normalizedMessage)) {
          setAssistantReply("");
          await acceptDraft({ ...priorVoiceSave.draft, employees: mentioned }, false);
          return;
        }
        switchingEmployee = true;
        priorVoiceSave = null;
        voiceSavedRef.current = null;
        pendingScheduleInputRef.current = "";
        setSelectedEmployee(null);
        setDraft(null);
      }
    }
    const fallbackDate = selectedMonth instanceof Date ? selectedMonth : new Date();
    const historyDrafts = chatHistory
      .filter((entry) => entry.type === "schedule" && !entry.saved)
      .map((entry) => entry.draft);
    const latestHistoryDraft = historyDrafts.at(-1);
    const contextDraft = switchingEmployee ? null : priorVoiceSave?.draft || draft || latestHistoryDraft || null;
    const previousDraft = draft ? latestHistoryDraft : historyDrafts.at(-2);
    if (/\b(?:ta bort|radera|stryk)\b/.test(normalizedMessage) && /\b(?:pass|passet)\b/.test(normalizedMessage)) {
      const restoreSavedCard = () => {
        if (priorVoiceSave) { setSaved(true); setSavedShiftIds(priorVoiceSave.records.map((record) => record.id)); }
      };
      restoreSavedCard();
      if (!priorVoiceSave?.records?.length) {
        setAssistantReply("Jag har inget sparat pass i samtalet att ta bort. Välj passet i schemat.");
        return;
      }
      const dates = parseDates(submittedMessage, fallbackDate);
      let candidates = priorVoiceSave.records.filter((record) => !dates.length || dates.includes(record.data.date));
      const exactProjects = findAllMentions(projects, submittedMessage, (project) => project.name);
      // A distinctive shared word can resolve a misheard project, but only uniquely.
      const projectWords = normalizedMessage.split(" ");
      const nearProjects = projects.filter((project) => normalize(project.name).split(" ").some((word) => word.length >= 5 && projectWords.includes(word)));
      const mentionedProjects = exactProjects.length ? exactProjects : nearProjects;
      if (mentionedProjects.length > 1) {
        setAssistantReply("Vilket projekt gäller passet? Säg hela projektnamnet och datumet igen."); return;
      }
      if (mentionedProjects.length) candidates = candidates.filter((record) => record.data.projectId === mentionedProjects[0].id);
      const unmatched = normalizedMessage.split(" ").filter((word) => !/^\d+$/.test(word) && !["ta", "bort", "radera", "stryk", "det", "detta", "pass", "passet", "fran", "den", "pa", "i", "tack", ...Object.keys(MONTHS), ...Object.keys(MONTH_ALIASES)].includes(word));
      if (!mentionedProjects.length && unmatched.length) {
        setAssistantReply("Jag kunde inte koppla projektnamnet till passet. Säg projektnamnet och datumet igen."); return;
      }
      if (candidates.length !== 1) {
        setAssistantReply(candidates.length ? "Vilket pass vill du ta bort? Ange datum och projekt." : "Jag hittar inget av de senaste passen med det datumet och projektet.");
        return;
      }
      const target = candidates[0];
      voiceBusyRef.current = true; setIsSaving(true); setAssistantReply("");
      try {
        await persistVoiceFollowup(db, companyUid, selectedSchema, [target], []);
        if (scopeRef.current !== inputScope) return;
        const remaining = priorVoiceSave.records.filter((record) => record.id !== target.id);
        const rows = priorVoiceSave.draft.rows.flatMap((row) => {
          const blocks = row.blocks.flatMap((block) => {
            const remainingProjects = block.projects.filter((project) => !(row.date === target.data.date && block.startTime === target.data.startTime && block.endTime === target.data.endTime && project.id === target.data.projectId));
            return remainingProjects.length ? [{ ...block, projects: remainingProjects }] : [];
          });
          return blocks.length ? [{ ...row, blocks }] : [];
        });
        const blocks = rows.flatMap((row) => row.blocks);
        const next = remaining.length ? { ...priorVoiceSave.draft, rows, blocks, dates: rows.map((row) => row.date), projects: [...new Map(blocks.flatMap((block) => block.projects).map((project) => [project.id, project])).values()] } : null;
        voiceSavedRef.current = next ? { draft: next, records: remaining } : null;
        setDraft(next); setSaved(Boolean(next)); setSavedShiftIds(remaining.map((record) => record.id));
        setChatHistory((history) => history.filter((entry) => !entry.savedShiftIds?.includes(target.id)));
        pendingScheduleInputRef.current = "";
        setMessage(""); setSentMessage(submittedMessage); setAssistantReply("Borttaget.");
      } catch (deleteError) {
        if (scopeRef.current === inputScope) setError(deleteError.message?.startsWith("Passet har") ? deleteError.message : "Passet kunde inte tas bort. Försök igen.");
      } finally { voiceBusyRef.current = false; setIsSaving(false); }
      return;
    }
    const dateCorrection = correctDraftDate(contextDraft, submittedMessage, fallbackDate);
    if (dateCorrection) {
      if (dateCorrection.error) { setAssistantReply(dateCorrection.error); return; }
      setAssistantReply(""); setSentMessage(submittedMessage); setMessage("");
      await acceptDraft(dateCorrection.draft, true);
      return;
    }

    if (fromVoice && priorVoiceSave && /\b(?:andra|byt|flytta)\b/.test(normalizedMessage)) {
      const changingProject = /\bprojekt\b/.test(normalizedMessage);
      const movingDate = /\bflytta\b/.test(normalizedMessage);
      if (changingProject || movingDate) {
        const matches = findAllMentions(projects, submittedMessage, (project) => project.name);
        const dates = parseDates(submittedMessage, fallbackDate);
        if (contextDraft.rows.length !== 1 || contextDraft.rows[0].blocks.length !== 1 || (changingProject && matches.length !== 1) || (movingDate && dates.length !== 1)) {
          setAssistantReply(changingProject ? "Ange ett entydigt projekt och pass att ändra." : "Ange ett entydigt pass och datum med dag och månad.");
          return;
        }
        const row = contextDraft.rows[0];
        const block = { ...row.blocks[0], projects: changingProject ? matches : row.blocks[0].projects };
        const date = movingDate ? dates[0] : row.date;
        setAssistantReply("");
        await acceptDraft({ ...contextDraft, rows: [{ date, blocks: [block] }], blocks: [block], dates: [date], projects: block.projects }, true);
        return;
      }
    }

    const employeeDraft = (!saved || priorVoiceSave)
      ? updateDraftEmployeesFollowup(contextDraft, submittedMessage, [UNSTAFFED_EMPLOYEE, ...employees])
      : null;
    if (employeeDraft) {
      if (fromVoice) { setAssistantReply(""); await acceptDraft(employeeDraft, true); return; }
      clearTimeout(thinkingTimeoutRef.current);
      setSentMessage(submittedMessage);
      setMessage("");
      setDraft(null);
      setAssistantReply("");
      setIsThinking(true);
      thinkingTimeoutRef.current = setTimeout(() => {
        setDraft(employeeDraft);
        setIsThinking(false);
      }, typeof voiceText === "string" ? 0 : 700);
      return;
    }
    const restoredDraft = (!saved || priorVoiceSave)
      ? restoreDraftFollowup(contextDraft, previousDraft, submittedMessage, fallbackDate)
      : null;
    if (restoredDraft) {
      if (fromVoice) { setAssistantReply(""); await acceptDraft(restoredDraft, true); return; }
      clearTimeout(thinkingTimeoutRef.current);
      setSentMessage(submittedMessage);
      setMessage("");
      setDraft(null);
      setAssistantReply("");
      setIsThinking(true);
      thinkingTimeoutRef.current = setTimeout(() => {
        setDraft(restoredDraft);
        setIsThinking(false);
      }, typeof voiceText === "string" ? 0 : 700);
      return;
    }
    const reducedDraft = (!saved || priorVoiceSave)
      ? removeFromDraftFollowup(contextDraft, submittedMessage, fallbackDate)
      : null;
    if (reducedDraft) {
      if (fromVoice) { setAssistantReply(""); await acceptDraft(reducedDraft, true); return; }
      clearTimeout(thinkingTimeoutRef.current);
      setSentMessage(submittedMessage);
      setMessage("");
      setDraft(null);
      setAssistantReply("");
      setIsThinking(true);
      thinkingTimeoutRef.current = setTimeout(() => {
        setDraft(reducedDraft);
        setIsThinking(false);
      }, typeof voiceText === "string" ? 0 : 700);
      return;
    }
    const extendedDraft = (!saved || priorVoiceSave)
      ? extendDraftFromFollowup(contextDraft, submittedMessage, projects, fallbackDate)
      : null;
    if (extendedDraft) {
      if (fromVoice) { setAssistantReply(""); await acceptDraft(extendedDraft, true); return; }
      clearTimeout(thinkingTimeoutRef.current);
      setSentMessage(submittedMessage);
      setMessage("");
      setDraft(null);
      setAssistantReply("");
      setIsThinking(true);
      thinkingTimeoutRef.current = setTimeout(() => {
        setDraft(extendedDraft);
        setIsThinking(false);
      }, typeof voiceText === "string" ? 0 : 700);
      return;
    }
    const correctedDraft = (!saved || priorVoiceSave) ? updateDraftFromFollowup(contextDraft, submittedMessage, fallbackDate) : null;
    if (correctedDraft) {
      if (fromVoice) { setAssistantReply(""); await acceptDraft(correctedDraft, true); return; }
      clearTimeout(thinkingTimeoutRef.current);
      setSentMessage(submittedMessage);
      setMessage("");
      setDraft(null);
      setAssistantReply("");
      setIsThinking(true);
      thinkingTimeoutRef.current = setTimeout(() => {
        setDraft(correctedDraft);
        setIsThinking(false);
      }, typeof voiceText === "string" ? 0 : 700);
      return;
    }
    const scheduleInput = pendingScheduleInputRef.current
      ? `${pendingScheduleInputRef.current}\n${submittedMessage}`
      : submittedMessage;
    const normalizedScheduleInput = normalize(scheduleInput);
    let matchedEmployees = findClosestEmployees(employees, scheduleInput);
    const exactEmployees = employees.filter((item) => ` ${normalize(scheduleInput)} `.includes(` ${normalize(item.name)} `));
    if (!matchedEmployees.length) {
      matchedEmployees = employees.filter((item) => {
        const firstName = normalize(item.name).split(" ")[0];
        return firstName.length >= 3 && ` ${normalize(scheduleInput)} `.includes(` ${firstName} `);
      });
    }
    if (!exactEmployees.length && matchedEmployees.length > 1) {
      pendingScheduleInputRef.current = scheduleInput;
      setSentMessage(submittedMessage); setMessage(""); setDraft(null); setIsThinking(false);
      setAssistantReply(`Jag är osäker på vem du menar. Menar du ${matchedEmployees.map((item) => item.name).join(" eller ")}? Säg hela namnet.`);
      return;
    }
    let selectedEmployees = !switchingEmployee && selectedEmployee && !matchedEmployees.some((item) => item.id === selectedEmployee.id)
      ? [...matchedEmployees, selectedEmployee]
      : matchedEmployees;
    if (/\b(?:obemannad|obemannade|obemmanad|obemanad)\b/.test(normalizedScheduleInput)) {
      selectedEmployees = [...new Map(
        [...selectedEmployees, UNSTAFFED_EMPLOYEE].map((employee) => [employee.id, employee])
      ).values()];
    }
    const selectedProjects = findAllMentions(projects, scheduleInput, (item) => item.name);
    if (selectedProjects.length > 1 && !projects.some((item) => ` ${normalize(scheduleInput)} `.includes(` ${normalize(item.name)} `))) {
      pendingScheduleInputRef.current = scheduleInput;
      setSentMessage(submittedMessage); setMessage(""); setDraft(null); setIsThinking(false);
      setAssistantReply(`Vilket projekt menar du: ${selectedProjects.map((item) => item.name).join(" eller ")}?`);
      return;
    }
    const monthlyWorkload = parseMonthlyWorkload(scheduleInput, fallbackDate);
    if (monthlyWorkload?.error) {
      pendingScheduleInputRef.current = scheduleInput;
      setSentMessage(submittedMessage); setMessage(""); setDraft(null); setAssistantReply(monthlyWorkload.error); setIsThinking(false);
      return;
    }
    const projectAllocations = monthlyWorkload ? parseProjectHourAllocations(scheduleInput, selectedProjects) : [];
    if (projectAllocations.length && projectAllocations.length !== selectedProjects.length) {
      const allocatedIds = new Set(projectAllocations.map((allocation) => allocation.project.id));
      const projectsWithoutHours = selectedProjects.filter((project) => !allocatedIds.has(project.id));
      pendingScheduleInputRef.current = scheduleInput;
      setSentMessage(submittedMessage); setMessage(""); setDraft(null);
      setAssistantReply(`Skriv hur många timmar som ska läggas på ${projectsWithoutHours.map((project) => project.name).join(" och ")}.`);
      setIsThinking(false);
      return;
    }
    const monthlySchedule = monthlyWorkload && selectedProjects.length
      ? createMonthlySchedule(monthlyWorkload, selectedProjects, projectAllocations)
      : null;
    if (monthlySchedule?.error) {
      pendingScheduleInputRef.current = scheduleInput;
      setSentMessage(submittedMessage); setMessage(""); setDraft(null); setAssistantReply(monthlySchedule.error); setIsThinking(false);
      return;
    }
    const rows = monthlySchedule?.rows || parseScheduleRows(scheduleInput, projects, fallbackDate);
    const dates = rows.map((row) => row.date);
    let blocks = rows.flatMap((row) => row.blocks);
    if (blocks.length === 1 && !blocks[0].projects.length) blocks = [{ ...blocks[0], projects: selectedProjects }];
    const startTime = blocks[0]?.startTime || "";
    const endTime = blocks[0]?.endTime || "";
    const missing = [];
    const mentionedTimes = [...String(scheduleInput).matchAll(/\b(?:[01]?\d|2[0-3])[:.]\d{2}\b/g)];
    const hasCompleteTimes = rows.length && rows.every((row) => row.blocks.length && row.blocks.every((block) => block.startTime && block.endTime));
    if (!selectedEmployees.length) missing.push("användare");
    if (!dates.length) missing.push(monthlyWorkload ? "månad" : "datum och månad");
    if (!monthlyWorkload && !hasCompleteTimes) {
      if (!mentionedTimes.length) missing.push("starttid", "sluttid");
      else if (mentionedTimes.length === 1) {
        const textBeforeTime = normalize(String(scheduleInput).slice(Math.max(0, mentionedTimes[0].index - 12), mentionedTimes[0].index));
        missing.push(/\btill$/.test(textBeforeTime) ? "starttid" : "sluttid");
      } else missing.push("starttid och sluttid i formatet 10:00-20:00");
    }
    if (!selectedProjects.length || rows.some((row) => row.blocks.some((block) => !block.projects.length))) missing.push("projekt");
    if (missing.length) {
      pendingScheduleInputRef.current = scheduleInput;
      setDraft(null);
      clearTimeout(thinkingTimeoutRef.current);
      setSentMessage(submittedMessage);
      setMessage("");
      setAssistantReply("");
      setIsThinking(true);
      thinkingTimeoutRef.current = setTimeout(() => {
        const uniqueMissing = [...new Set(missing)];
        const missingText = uniqueMissing.length > 1
          ? `${uniqueMissing.slice(0, -1).join(", ")} och ${uniqueMissing.at(-1)}`
          : uniqueMissing[0];
        const questions = { användare: "Vem ska arbeta? Säg för- och efternamn.", "datum och månad": "Vilket datum och vilken månad ska personen arbeta?", månad: "Vilken månad gäller schemat?", starttid: "Vilken tid ska arbetspasset börja?", sluttid: "Vilken tid ska arbetspasset sluta?", projekt: "Vilket projekt ska personen arbeta på?" };
        const understood = selectedEmployees.length ? `Jag har uppfattat ${selectedEmployees.map((item) => item.name).join(" och ")}. ` : "";
        setAssistantReply(`${understood}${questions[uniqueMissing[0]] || `Vilka tider gäller? Jag behöver ${missingText}.`}`);
        setIsThinking(false);
      }, typeof voiceText === "string" ? 0 : 700);
      return;
    }
    pendingScheduleInputRef.current = "";
    const inferredMonthOnly = dates.length === 1 && !/\b\d{1,2}(?:a|e)?\s+(?:januari|februari|mars|april|maj|juni|juli|augusti|september|oktober|november|december)\b/i.test(scheduleInput)
      && /\b(?:januari|februari|mars|april|maj|juni|juli|augusti|september|oktober|november|december)\b/i.test(scheduleInput);
    const nextDraft = {
      employees: selectedEmployees, projects: selectedProjects, dates, blocks, rows, startTime, endTime, inferredMonthOnly,
      monthlyWorkload: monthlyWorkload ? { ...monthlyWorkload, totalHours: monthlySchedule.totalHours, projectAllocations, weekendCount: monthlySchedule.weekendCount } : null,
    };
    if (fromVoice) { setSentMessage(inputMessage.trim()); setAssistantReply(""); setMessage(""); await acceptDraft(nextDraft); return; }
    clearTimeout(thinkingTimeoutRef.current);
    setSentMessage(inputMessage.trim());
    setDraft(null);
    setAssistantReply("");
    setMessage("");
    setIsThinking(true);
    thinkingTimeoutRef.current = setTimeout(() => {
      setDraft(nextDraft);
      setIsThinking(false);
    }, typeof voiceText === "string" ? 0 : 900);
  };


  const fetchExistingSchedule = async () => {
    if (!companyUid || !fetchEmployeeIds.length) {
      setError("Välj minst en anställd först.");
      return;
    }
    const selectedEmployees = employees.filter((item) => fetchEmployeeIds.includes(item.id));
    if (selectedEmployees.length !== fetchEmployeeIds.length) { setError("Användaren finns inte i det valda schemat."); return; }
    setError("");
    setIsFetchingSchedule(true);
    try {
      const snapshot = await getDocs(collection(db, "users", companyUid, "shifts"));
      const rows = snapshot.docs.flatMap((shiftDoc) => {
        const shift = shiftDoc.data();
        const assignedEmployeeIds = [
          shift.employeeId,
          shift.userId,
          shift.assignedEmployeeId,
          ...(Array.isArray(shift.employees)
            ? shift.employees.flatMap((item) => [item?.employeeId, item?.id, item?.userId])
            : []),
        ].filter(Boolean);
        const matchingEmployees = selectedEmployees.filter((employee) => assignedEmployeeIds.includes(employee.id));
        if (!matchingEmployees.length) return [];
        if (selectedSchema && shift.schemaId && shift.schemaId !== selectedSchema) return [];

        const rawDate = shift.date?.toDate ? shift.date.toDate() : shift.date;
        const parsedDate = rawDate instanceof Date ? rawDate : new Date(`${rawDate}T12:00:00`);
        const year = Number(shift.year) || parsedDate.getFullYear();
        const month = Number(shift.month) || parsedDate.getMonth() + 1;
        if (year !== Number(fetchYear) || month !== Number(fetchMonth) || Number.isNaN(parsedDate.getTime())) return [];
        const date = typeof shift.date === "string"
          ? shift.date.slice(0, 10)
          : `${year}-${String(month).padStart(2, "0")}-${String(parsedDate.getDate()).padStart(2, "0")}`;
        return [{
          id: shiftDoc.id,
          employeeIds: matchingEmployees.map((employee) => employee.id),
          date,
          startTime: shift.startTime || String(shift.time || "").split("-")[0]?.trim() || "",
          endTime: shift.endTime || String(shift.time || "").split("-")[1]?.trim() || "",
          project: shift.project || projects.find((item) => item.id === shift.projectId)?.name || "Projekt saknas",
          projectId: shift.projectId || "",
        }];
      }).sort((left, right) => `${left.date} ${left.startTime}`.localeCompare(`${right.date} ${right.startTime}`));

      setFetchedSchedule({ month: Number(fetchMonth), year: Number(fetchYear), rows, schedules: selectedEmployees.map((employee) => ({ employee, month: Number(fetchMonth), year: Number(fetchYear), rows: rows.filter((row) => row.employeeIds.includes(employee.id)) })) });
      setActiveFetchedEmployeeId(selectedEmployees[0]?.id || "");
      const nextMonthDate = new Date(Number(fetchYear), Number(fetchMonth), 1);
      setCopyScheduleMonth(nextMonthDate.getMonth() + 1);
      setCopyScheduleYear(nextMonthDate.getFullYear());
    } catch (fetchError) {
      console.error("Kunde inte hämta tidigare schema:", fetchError);
      setError("Schemat kunde inte hämtas. Försök igen.");
    } finally {
      setIsFetchingSchedule(false);
    }
  };

  const createCopiedScheduleDraft = () => {
    const schedule = fetchedSchedule;
    const selectedSchedule = schedule?.schedules.find((item) => item.employee.id === activeFetchedEmployeeId) || schedule?.schedules[0];
    if (!selectedSchedule?.rows?.length) return;
    const copyWarnings = [];
    const daysInTargetMonth = new Date(copyScheduleYear, copyScheduleMonth, 0).getDate();
    const rows = selectedSchedule.rows.flatMap((sourceRow) => {
      const day = Number(sourceRow.date.slice(-2));
      const targetDate = new Date(copyScheduleYear, copyScheduleMonth - 1, day);
      if (targetDate.getMonth() !== copyScheduleMonth - 1) {
        const [startHours, startMinutes] = String(sourceRow.startTime || "0:0").split(":").map(Number);
        const [endHours, endMinutes] = String(sourceRow.endTime || "0:0").split(":").map(Number);
        let durationMinutes = (endHours * 60 + endMinutes) - (startHours * 60 + startMinutes);
        if (durationMinutes < 0) durationMinutes += 24 * 60;
        copyWarnings.push({
          sourceDate: sourceRow.date,
          sourceLabel: new Date(`${sourceRow.date}T12:00:00`).toLocaleDateString("sv-SE", { day: "numeric", month: "long" }),
          targetMonthName: Object.keys(MONTHS)[copyScheduleMonth - 1].replace(/^./, (letter) => letter.toLocaleUpperCase("sv-SE")),
          daysInTargetMonth,
          startTime: sourceRow.startTime,
          endTime: sourceRow.endTime,
          project: sourceRow.project,
          hours: Number((durationMinutes / 60).toFixed(2)).toLocaleString("sv-SE"),
        });
        return [];
      }
      const project = projects.find((item) => item.id === sourceRow.projectId)
        || projects.find((item) => normalize(item.name) === normalize(sourceRow.project))
        || { id: sourceRow.projectId || sourceRow.project, name: sourceRow.project };
      return [{
        employeeIds: [selectedSchedule.employee.id],
        date: `${copyScheduleYear}-${String(copyScheduleMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
        blocks: [{ startTime: sourceRow.startTime, endTime: sourceRow.endTime, projects: [project] }],
      }];
    });
    const blocks = rows.flatMap((row) => row.blocks);
    const copiedProjects = [...new Map(blocks.flatMap((block) => block.projects).map((project) => [project.id, project])).values()];
    setSaved(false);
    setSavedShiftIds([]);
    setDraft({
      employees: [selectedSchedule.employee],
      projects: copiedProjects,
      dates: rows.map((row) => row.date),
      blocks,
      rows,
      startTime: blocks[0]?.startTime || "",
      endTime: blocks[0]?.endTime || "",
      copiedFrom: { month: schedule.month, year: schedule.year },
      copyWarnings,
    });
    setError("");
  };

  const resetChat = () => {
    voiceSavedRef.current = null;
    stopVoice();
    clearTimeout(thinkingTimeoutRef.current);
    setMessage("");
    setSentMessage("");
    setAssistantReply("");
    setChatHistory([]);
    setSelectedEmployee(null);
    setDraft(null);
    setError("");
    setSaved(false);
    setSavedShiftIds([]);
    setIsThinking(false);
    setShowScheduleFetcher(false); setShowScheduleBuilder(false);
    setFetchedSchedule(null);
    pendingScheduleInputRef.current = "";
    requestAnimationFrame(() => messageRef.current?.focus());
  };

  const approveShift = async (draftToSave = draft, historyIndex = null, previousRecords = null) => {
    if (!draftToSave || !companyUid || !currentUser?.uid || saveInFlightRef.current) return;
    if (!selectedSchema) { setError("Välj först ett schema på schemasidan."); return; }
    const currentMembership = allowedEmployeesRef.current;
    if (currentMembership.schemaId !== selectedSchema || !Array.isArray(currentMembership.employeeIds)) {
      setError("Vänta tills schemats användare har hämtats."); return;
    }
    const saveScope = scopeRef.current;
    const employeesToSave = draftToSave.employees.filter((employee) => employee.id);
    if (!employeesToSave.length) { setError("Välj minst en anställd innan du lägger till passet."); return; }
    const allowedEmployeeIds = new Set(currentMembership.employees.map((employee) => employee.id));
    if (employeesToSave.some((employee) => !employee.isObemannad && !allowedEmployeeIds.has(employee.id))) {
      setError("En vald användare finns inte längre i schemat. Välj en användare i schemat.");
      return;
    }
    saveInFlightRef.current = true;
    setIsSaving(true); setError("");
    try {
      if (!draftToSave.rows.length || draftToSave.rows.some((row) => !row.blocks.length || row.blocks.some((block) => !block.projects.length))) throw new Error("Ofullständigt pass.");
      if (draftToSave.rows.some(row => row.blocks.some(block => block.projects.some(project => !projects.some(visible => visible.id === project.id))))) {
        throw new Error("Projektet är inte längre tillgängligt. Välj ett aktivt projekt.");
      }
      const batch = writeBatch(db);
      const payloads = [];
      const createdShifts = draftToSave.rows.flatMap((row) => row.blocks.flatMap((block) => block.projects.map((project) => {
        const rowEmployees = employeesForScheduleRow(draftToSave, row);
        if (!rowEmployees.length) throw new Error("Passet har ingen vald anställd.");
        const hasUnstaffedEmployee = rowEmployees.some((employee) => employee.isObemannad);
        const firstStaffedEmployee = rowEmployees.find((employee) => !employee.isObemannad);
        const date = row.date;
        const parsedDate = new Date(`${date}T12:00:00`);
        const shiftRef = doc(collection(db, "users", companyUid, "shifts"));
        validateSlot(date, block.startTime, block.endTime);
        const payload = {
        schemaId: selectedSchema,
        project: project.name,
        projectId: project.id,
        date,
        year: parsedDate.getFullYear(),
        month: parsedDate.getMonth() + 1,
        startTime: block.startTime,
        endTime: block.endTime,
        time: `${block.startTime} - ${block.endTime}`,
        employees: rowEmployees.map((employee) => ({
          employeeId: employee.isObemannad ? "" : employee.id,
          name: employee.isObemannad ? "Obemannad" : employee.name,
          isObemannad: Boolean(employee.isObemannad),
          overtime: [], absences: [], breaks: [], isBooked: false, isApproved: false,
          bookingId: null, originalEmployeeId: null,
        })),
        employee: rowEmployees.map((employee) => employee.name).join(", "),
        employeeId: firstStaffedEmployee?.id || null,
        isObemannad: hasUnstaffedEmployee,
        isSelfBookable: hasUnstaffedEmployee,
        bookingType: hasUnstaffedEmployee ? "self_booking" : null,
        message: "",
        published: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: currentUser.uid,
        };
        payloads.push(payload);
        if (!previousRecords) batch.set(shiftRef, payload);
        return shiftRef;
      })));
      if (payloads.length > 400) throw new Error("För många pass.");
      let records;
      if (previousRecords) records = await persistVoiceFollowup(db, companyUid, selectedSchema, previousRecords, payloads);
      else { await batch.commit(); records = createdShifts.map((ref, index) => ({ id: ref.id, data: payloads[index] })); }
      if (saveScope !== scopeRef.current) return;
      const createdShiftIds = records.map((item) => item.id);
      if (previousRecords) setChatHistory((history) => history.filter((entry) => !entry.savedShiftIds?.some((id) => previousRecords.some((item) => item.id === id))));
      if (historyIndex === null) { setSaved(true); setSavedShiftIds(createdShiftIds); }
      else setChatHistory((history) => history.map((entry, index) => index === historyIndex ? { ...entry, saved: true, savedShiftIds: createdShiftIds } : entry));
      setMessage("");
      return records;
    } catch (saveError) {
      console.error("Kunde inte skapa AI-passet:", saveError);
      setError(saveError.message?.startsWith("Passet har") ? saveError.message : "Passet kunde inte sparas. Försök igen.");
    } finally { saveInFlightRef.current = false; setIsSaving(false); }
  };

  const undoShift = async (shiftIds, historyIndex = null) => {
    if (!companyUid || !shiftIds?.length) return;
    setIsSaving(true); setError("");
    try {
      await Promise.all(shiftIds.map((shiftId) => deleteDoc(doc(db, "users", companyUid, "shifts", shiftId))));
      if (historyIndex === null) { setSaved(false); setSavedShiftIds([]); }
      else setChatHistory((history) => history.map((entry, index) => index === historyIndex ? { ...entry, saved: false, savedShiftIds: [] } : entry));
    } catch (undoError) {
      console.error("Kunde inte återkalla AI-passet:", undoError);
      setError("Passet kunde inte återkallas. Försök igen.");
    } finally { setIsSaving(false); }
  };

  useEffect(() => { submitVoiceRef.current = interpretMessage; });

  if (!isOpen) return null;

  return (
    <aside className={`ai-panel ${isResizing ? "is-resizing" : ""}`} style={{ width: panelWidth }} aria-label="AI-schemaläggare">
      <button type="button" className="resize-handle" onPointerDown={(event) => { event.preventDefault(); setIsResizing(true); }} onDoubleClick={() => setPanelWidth(420)} aria-label="Ändra panelens bredd" title="Dra för att ändra storlek. Dubbelklicka för att återställa."><span /><span /><span /></button>
      <header className="ai-header">
        <div className="ai-header-top"><h2><FaMagic /> AI-schemaläggare</h2><div className="header-actions"><button onClick={resetChat} className="icon-button refresh-chat-button" aria-label="Starta om chatten" title="Starta om chatten"><FaRedoAlt /></button><button onClick={onClose} className="icon-button" aria-label="Stäng"><FaTimes /></button></div></div>
        <div className="header-actions"><button onClick={toggleVoiceInput} className={`icon-button voice-chat-button ${isListening ? "is-listening" : ""}`} aria-label={voiceActive ? "Avsluta röstsamtalet" : "Starta röstsamtal"} title={voiceActive ? "Avsluta röstsamtalet" : "Starta röstsamtal"} aria-pressed={voiceActive}><FaMicrophone /></button><button onClick={() => { setShowScheduleFetcher((value) => !value); setShowScheduleBuilder(false); setFetchYear(new Date().getFullYear()); setError(""); }} className={`icon-button fetch-schedule-button ${showScheduleFetcher ? "is-active" : ""}`} aria-label="Hämta befintligt schema" title="Hämta befintligt schema" aria-pressed={showScheduleFetcher}><FaCalendarAlt /></button><button type="button" className={`icon-button fetch-schedule-button ${showScheduleBuilder ? "is-active" : ""}`} aria-label="Återanvänd pass" title="Återanvänd pass" aria-pressed={showScheduleBuilder} disabled={isSaving || isThinking} onClick={() => { setShowScheduleBuilder((value) => !value); setShowScheduleFetcher(false); setError(""); }}><FaPen /></button></div>
      </header>
      <div className="ai-body">
        <div className="chat-shell">
          <div className="chat-messages" ref={chatMessagesRef} aria-live="polite">
            {!showScheduleBuilder && !showScheduleFetcher && !chatHistory.length && !sentMessage && !draft && !saved && <div className="chat-welcome"><div className="assistant-avatar"><FaMagic /></div><div><strong>Vad vill du lägga in?</strong><span>Beskriv vem som ska arbeta, projekt, datum och tider – eller be om ett månadsschema med procent eller totalt antal timmar.</span></div></div>}
            {showScheduleBuilder && <QuickShiftPanel companyUid={companyUid} employees={employees} projects={projects} initialEmployeeId={initialEmployeeId} onSelect={onSelectTemplate} />}
            {showScheduleBuilder && selectedTemplate && <div className="schedule-fetcher" role="status"><strong>{selectedTemplate.name} · {selectedTemplate.startTime}–{selectedTemplate.endTime}</strong><span>Tryck på + på en dag i schemat för att lägga in passet.</span><button type="button" className="edit-chat" onClick={() => onSelectTemplate(null)}>Avsluta placering</button></div>}
            {showScheduleFetcher && <div className="schedule-fetcher" role="dialog" aria-label="Hämta schema">
              <div className="schedule-fetcher-title"><FaCalendarAlt /><div><strong>Hämta schema</strong><span>Kryssa i anställda och välj månad</span></div></div>
              <label>Sök anställda<input type="search" value={fetchEmployeeSearch} onChange={(event) => setFetchEmployeeSearch(event.target.value)} placeholder="Sök namn…" /></label>
              <span>{fetchEmployeeIds.length} anställda valda</span>
              <fieldset className="fetch-employees"><legend>Anställda</legend>
                {matchingFetchEmployees.map((employee) => <label key={employee.id}><input type="checkbox" checked={fetchEmployeeIds.includes(employee.id)} onChange={(event) => { const checked = event.target.checked; setFetchEmployeeIds((ids) => checked ? [...ids, employee.id] : ids.filter((id) => id !== employee.id)); }} /><span>{employee.name}</span></label>)}
                {!matchingFetchEmployees.length && <span>{employees.length ? "Inga anställda matchar sökningen." : "Inga anställda finns i det valda schemat."}</span>}
              </fieldset>
              <div className="schedule-fetcher-period">
                <label>Månad<select value={fetchMonth} onChange={(event) => setFetchMonth(Number(event.target.value))}>{Object.entries(MONTHS).map(([monthName, monthNumber]) => <option key={monthName} value={monthNumber}>{monthName}</option>)}</select></label>
                <label>År<select value={fetchYear} onChange={(event) => setFetchYear(Number(event.target.value))}>{Array.from({ length: 7 }, (_, index) => new Date().getFullYear() - 3 + index).map((year) => <option key={year} value={year}>{year}</option>)}</select></label>
              </div>
              <div className="schedule-fetcher-actions"><button type="button" onClick={() => setShowScheduleFetcher(false)}>Avbryt</button><button type="button" className="fetch-confirm" onClick={fetchExistingSchedule} disabled={isFetchingSchedule || !fetchEmployeeIds.length}>{isFetchingSchedule ? "Hämtar…" : "Hämta"}</button></div>
            </div>}
            {!showScheduleBuilder && !showScheduleFetcher && <>{chatHistory.map((entry, historyIndex) => entry.type === "user" ? (
              <div className="user-message" key={`history-${historyIndex}`}>{entry.text}</div>
            ) : entry.type === "assistant" ? (
              <div className="conversation-reply" key={`history-${historyIndex}`}><div className="assistant-avatar"><FaMagic /></div><p>{entry.text}</p></div>
            ) : (
              <ScheduleChatBubble key={`history-${historyIndex}`} draft={entry.draft} employees={employees} projects={projects} saved={entry.saved} isSaving={isSaving} onChange={(nextDraft) => setChatHistory((history) => history.map((item, index) => index === historyIndex ? { ...item, draft: nextDraft } : item))} onApprove={() => approveShift(entry.draft, historyIndex)} onUndo={() => undoShift(entry.savedShiftIds, historyIndex)} />
            ))}
            {sentMessage && <div className="user-message">{sentMessage}</div>}
            {isThinking && <div className="thinking-message" aria-label="AI-schemaläggaren tänker">
              <div className="assistant-avatar"><FaMagic /></div>
              <div className="thinking-dots"><span /><span /><span /></div>
            </div>}
            {assistantReply && <div className="conversation-reply">
              <div className="assistant-avatar"><FaMagic /></div>
              <p>{assistantReply}</p>
            </div>}
            {draft && <ScheduleChatBubble draft={draft} employees={employees} projects={projects} saved={saved} isSaving={isSaving} onChange={setDraft} onApprove={() => approveShift()} onUndo={() => undoShift(savedShiftIds)} />}{error && <div className="error" role="alert">{error}</div>}</>}
            {showScheduleFetcher && <>{fetchedSchedule?.schedules.length > 1 && <div className="fetched-schedule-tabs" role="tablist" aria-label="Vald personal" style={{ display: "flex", gap: 6, overflowX: "auto", borderBottom: "1px solid #e2e8f0" }}>{fetchedSchedule.schedules.map((schedule) => <button key={schedule.employee.id} type="button" role="tab" aria-selected={activeFetchedEmployeeId === schedule.employee.id} onClick={() => setActiveFetchedEmployeeId(schedule.employee.id)} style={{ flex: "none", border: 0, borderBottom: activeFetchedEmployeeId === schedule.employee.id ? "2px solid #6323ef" : "2px solid transparent", background: "transparent", padding: "8px 11px", color: activeFetchedEmployeeId === schedule.employee.id ? "#6323ef" : "#64748b", font: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{schedule.employee.name}</button>)}</div>}
            {fetchedSchedule?.schedules.filter((schedule) => schedule.employee.id === activeFetchedEmployeeId).map((schedule) => <div key={schedule.employee.id} className="fetched-schedule"><div className="assistant-avatar"><FaMagic /></div><div className="fetched-schedule-content"><strong>{schedule.employee?.name || "Användare"}</strong><span className="fetched-schedule-month">{Object.keys(MONTHS)[schedule.month - 1]} {schedule.year}</span>{schedule.rows.length ? schedule.rows.map((row) => <span className="fetched-shift" key={row.id}>{new Date(`${row.date}T12:00:00`).toLocaleDateString("sv-SE", { weekday: "short", day: "numeric", month: "short" })} · {row.startTime}-{row.endTime} · {row.project}</span>) : <span className="fetched-empty">Inga pass hittades för den valda månaden.</span>}</div></div>)}
            {fetchedSchedule?.schedules.some((schedule) => schedule.employee.id === activeFetchedEmployeeId && schedule.rows.length > 0) && <div className="copy-schedule-question"><div className="assistant-avatar"><FaMagic /></div><div className="copy-schedule-content"><strong>Vill du lägga till samma schema?</strong><span>Välj vilken månad och vilket år schemat ska kopieras till.</span><div className="copy-schedule-period"><label>Månad<select value={copyScheduleMonth} onChange={(event) => setCopyScheduleMonth(Number(event.target.value))}>{Object.entries(MONTHS).map(([monthName, monthNumber]) => <option key={monthName} value={monthNumber}>{monthName}</option>)}</select></label><label>År<select value={copyScheduleYear} onChange={(event) => setCopyScheduleYear(Number(event.target.value))}>{Array.from({ length: 7 }, (_, index) => new Date().getFullYear() - 3 + index).map((year) => <option key={year} value={year}>{year}</option>)}</select></label></div><button type="button" onClick={createCopiedScheduleDraft}>Skapa schema för {fetchedSchedule.schedules.find((schedule) => schedule.employee.id === activeFetchedEmployeeId)?.employee.name || "vald person"}</button></div></div>}
            {draft && <ScheduleChatBubble draft={draft} employees={employees} projects={projects} saved={saved} isSaving={isSaving} onChange={setDraft} onApprove={() => approveShift()} onUndo={() => undoShift(savedShiftIds)} />}
            {error && <div className="error" role="alert">{error}</div>}</>}
          </div>
          <div className="composer-area">
            {employeeSuggestions.length > 0 && <div className="employee-suggestions"><span>Menar du?</span>{employeeSuggestions.map((employee) => <button key={employee.id} type="button" onClick={() => { insertSuggestion(employee.name); setSelectedEmployee(employee); }}>{employee.name}</button>)}</div>}
            {projectSuggestions.length > 0 && <div className="project-suggestions"><span>Lägg till projekt:</span>{projectSuggestions.map((project) => <button key={project.id} type="button" onClick={() => insertSuggestion(project.name)}>{project.name}</button>)}</div>}
            {timeSuggestion && <div className="time-suggestion"><span>Menar du tiden?</span><button type="button" onClick={() => insertTimeSuggestion(timeSuggestion)}>{timeSuggestion}</button></div>}
            {monthSuggestions.length > 0 && <div className="month-suggestions"><span>Menar du månaden?</span><div>{monthSuggestions.map((month) => <button key={month} type="button" onClick={() => insertSuggestion(month)}>{month}</button>)}</div></div>}
            {voiceActive && !isSpeaking && !isListening && <div className="voice-listening-status" role="status">Bearbetar det du sa… <button type="button" onClick={stopVoice}>Avsluta samtalet</button></div>}
            {isSpeaking && <div className="voice-listening-status" role="status">Lunero pratar… <button type="button" onClick={stopVoice}>Stoppa röstsvar</button></div>}
            {voicePaused && <div className="voice-listening-status" role="status">Pausad. Börja prata igen när du är redo.</div>}
            {isListening && !isSpeaking && !voicePaused && <div className="voice-listening-status" role="status"><span />Lyssnar med webbläsarens röstfunktion… prata som vanligt. Säg ”stopp” för att tysta rösten, eller ”avsluta samtalet” för att stänga mikrofonen.</div>}
            <div className="chat-composer">
              <textarea ref={messageRef} id="schedule-message" value={message} onChange={(event) => { setMessage(event.target.value); setSelectedEmployee(null); }}
                placeholder="Skriv här…" rows={2} autoFocus />
              <button className="interpret-button" onClick={interpretMessage} disabled={isThinking || !message.trim()} aria-label="Skicka" title="Skicka"><FaPaperPlane /></button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .ai-panel{position:fixed;right:0;top:0;width:420px;max-width:100vw;height:100vh;background:#fff;box-shadow:-8px 0 30px rgba(15,23,42,.16);z-index:9999;display:flex;flex-direction:column;color:#0f172a}.ai-header{position:relative;min-height:32px;padding:20px;background:linear-gradient(135deg,#f5f3ff,#eef2ff);border-bottom:1px solid #ddd6fe;display:flex;flex-direction:column;align-items:flex-start;gap:12px;flex-shrink:0}.ai-header-top{display:flex;align-items:center;justify-content:space-between;gap:12px;width:100%}.ai-header-top h2{min-width:0}.ai-header .header-actions{margin-left:0;flex-wrap:wrap}.ai-header-top .header-actions{flex-wrap:nowrap;flex-shrink:0}.ai-header h2{font-size:18px;margin:0;display:flex;gap:9px;align-items:center}.ai-header h2 svg{color:#6323ef}.ai-header p{font-size:12px;color:#64748b;margin:5px 0 0}.icon-button{border:0;background:#fff;width:32px;height:32px;border-radius:8px;display:grid;place-items:center;cursor:pointer;color:#475569;box-shadow:0 1px 3px rgba(15,23,42,.1)}.ai-body{padding:22px;overflow:auto}.intro{display:flex;flex-direction:column;gap:7px;padding:16px;border:1px solid #e9d5ff;background:#faf5ff;border-radius:12px;margin-bottom:22px}.intro strong{font-size:14px}.intro span{font-size:12px;line-height:1.5;color:#64748b}.message-label{display:block;font-size:12px;font-weight:700;margin:0 0 7px}textarea{box-sizing:border-box;width:100%;resize:vertical;min-height:145px;border:1px solid #cbd5e1;border-radius:12px;padding:13px;font:inherit;font-size:14px;line-height:1.5}textarea:focus{outline:0;border-color:#6323ef;box-shadow:0 0 0 3px rgba(99,35,239,.12)}.employee-suggestions{margin-top:8px;padding:10px;border:1px solid #ddd6fe;border-radius:9px;background:#faf5ff;display:flex;flex-direction:column;gap:6px}.employee-suggestions>span{font-size:11px;color:#64748b}.employee-suggestions button{text-align:left;border:1px solid #e2e8f0;border-radius:7px;background:#fff;padding:8px 10px;font-size:12px;font-weight:700;cursor:pointer}.employee-suggestions button:hover{border-color:#6323ef;color:#6323ef}.interpret-button{margin:12px 0 0 auto;width:42px;height:42px;border:0;border-radius:50%;background:#6323ef;color:#fff;display:grid;place-items:center;cursor:pointer;box-shadow:0 4px 12px rgba(99,35,239,.25)}.interpret-button:hover{background:#5218d3}.interpret-button:focus-visible{outline:3px solid rgba(99,35,239,.25);outline-offset:2px}.hint{font-size:11px;line-height:1.45;color:#94a3b8}.error,.success{padding:12px;border-radius:9px;font-size:12px;line-height:1.45;margin-top:14px}.error{background:#fef2f2;color:#b91c1c;border:1px solid #fecaca}.success{background:#ecfdf5;color:#047857;border:1px solid #a7f3d0;display:flex;gap:8px;align-items:center}.chat-response{margin-top:18px;border-top:1px solid #e2e8f0;padding-top:4px}.assistant-response{display:flex;gap:10px;margin:14px 0;padding:14px;border-radius:12px;background:#f8fafc;border:1px solid #e2e8f0}.assistant-avatar{flex:0 0 28px;width:28px;height:28px;border-radius:50%;display:grid;place-items:center;background:#6323ef;color:#fff}.assistant-avatar svg{width:12px}.assistant-message{display:flex;flex:1;min-width:0;flex-direction:column;gap:5px;font-size:13px;line-height:1.45}.assistant-message strong{font-size:14px;margin-bottom:2px}.assistant-message span{color:#334155}.assistant-message small{margin-top:5px;color:#92400e;line-height:1.4}.approve-chat{display:flex;align-items:center;justify-content:center;gap:7px;margin-top:12px;width:100%;border:0;border-radius:9px;padding:11px 12px;background:#16a34a;color:#fff;font-weight:800;cursor:pointer}.approve-chat:hover{background:#15803d}.approve-chat:disabled{cursor:wait;opacity:.65}.approve-chat.is-added,.approve-chat.is-added:hover{background:#047857;cursor:default;opacity:1}@media(max-width:480px){.ai-panel{width:100vw}}
        .review-title p{margin:6px 20px 0 0;color:#64748b;font-size:12px}.edit-grid{display:flex;flex-direction:column;gap:12px;margin:22px 0}.edit-grid label{display:flex;flex-direction:column;gap:6px;font-size:12px;font-weight:700;color:#475569}.edit-grid input,.edit-grid select{box-sizing:border-box;width:100%;height:42px;border:1px solid #cbd5e1;border-radius:8px;background:#fff;padding:0 10px;font:inherit;color:#0f172a}.time-fields{display:grid;grid-template-columns:1fr 1fr;gap:10px}
        .matched-project{display:flex;align-items:center;gap:7px;margin-top:8px;padding:9px 10px;border:1px solid #bfdbfe;border-radius:8px;background:#eff6ff;color:#1d4ed8;font-size:12px}.project-suggestions,.shift-suggestions{margin-top:8px;padding:10px;border:1px solid #dbeafe;border-radius:9px;background:#f8fafc;display:flex;flex-direction:column;gap:6px}.project-suggestions>span,.shift-suggestions>span{font-size:11px;color:#64748b;font-weight:700}.project-suggestions button,.shift-suggestions button{text-align:left;border:1px solid #e2e8f0;border-radius:7px;background:#fff;padding:8px 10px;font-size:12px;font-weight:700;cursor:pointer}.project-suggestions button:hover,.shift-suggestions button:hover{border-color:#3b82f6;color:#1d4ed8}.shift-suggestions button{display:flex;flex-direction:column;gap:3px}.shift-suggestions small{font-size:10px;color:#64748b;font-weight:500}
        .summary-counts{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin:18px 0}.summary-counts div{padding:11px 8px;border-radius:10px;background:#f5f3ff;text-align:center}.summary-counts strong{display:block;font-size:18px;color:#6323ef}.summary-counts span{font-size:10px;color:#64748b}.summary-sections{display:flex;flex-direction:column;gap:10px;margin-bottom:20px}.summary-sections section{border:1px solid #e2e8f0;border-radius:11px;padding:13px}.summary-sections h4{margin:0 0 9px;font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#64748b}.person-chips{display:flex;flex-wrap:wrap;gap:6px}.person-chips span{padding:6px 9px;border-radius:999px;background:#ecfdf5;color:#047857;font-size:12px;font-weight:700}.date-row{display:flex;justify-content:space-between;gap:12px;padding:7px 0;border-bottom:1px solid #f1f5f9;font-size:12px}.date-row:last-child{border-bottom:0}.date-row strong{text-transform:capitalize}.date-row span{color:#6323ef;font-weight:800;white-space:nowrap}.project-summary{font-size:13px;font-weight:800}.employee-editor{border:1px solid #cbd5e1;border-radius:9px;padding:10px;max-height:150px;overflow:auto}.employee-editor legend{font-size:12px;font-weight:700;color:#475569;padding:0 5px}.employee-editor label{flex-direction:row;align-items:center;padding:5px 2px}.employee-editor input{width:16px;height:16px}.employee-editor span{color:#0f172a;font-size:12px}
        .summary-counts{grid-template-columns:repeat(4,1fr)}.project-chips{display:flex;flex-wrap:wrap;gap:6px}.project-chips span{padding:6px 9px;border-radius:999px;background:#eff6ff;color:#1d4ed8;font-size:12px;font-weight:700}.date-row{display:block}.date-main{display:flex;justify-content:space-between;gap:12px}.date-main>span{color:#6323ef;font-weight:800;white-space:nowrap}.date-projects{display:flex;flex-wrap:wrap;gap:5px;margin-top:7px}.date-projects span{padding:4px 7px;border-radius:6px;background:#eff6ff;color:#1d4ed8;font-size:10px;font-weight:700}
        .date-heading{display:block;margin-bottom:7px}.work-block-row{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:7px 0;border-top:1px solid #f1f5f9}.block-time{color:#6323ef;font-weight:800;white-space:nowrap}.work-block-row .date-projects{justify-content:flex-end;margin-top:0}.block-editor{border:1px solid #c4b5fd;border-radius:10px;padding:12px}.block-editor legend{padding:0 6px;color:#6323ef;font-size:12px;font-weight:800}.block-project-options{display:flex;flex-wrap:wrap;gap:7px;margin-top:10px}.block-project-options label{flex-direction:row;align-items:center;border:1px solid #e2e8f0;border-radius:7px;padding:6px 8px}.block-project-options input{width:15px;height:15px}
        .inference-note{display:flex;flex-direction:column;gap:4px;margin-top:14px;padding:10px 12px;border:1px solid #fde68a;border-radius:9px;background:#fffbeb;color:#92400e}.inference-note strong{font-size:12px}.inference-note span{font-size:11px;line-height:1.4}
        .time-suggestion{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:8px;padding:9px 10px;border:1px solid #fed7aa;border-radius:9px;background:#fff7ed}.time-suggestion span{font-size:11px;color:#9a3412;font-weight:700}.time-suggestion button{border:1px solid #fdba74;border-radius:7px;background:#fff;padding:6px 12px;color:#c2410c;font-size:12px;font-weight:800;cursor:pointer}.time-suggestion button:hover{background:#ffedd5}
        .month-suggestions{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:8px;padding:9px 10px;border:1px solid #bae6fd;border-radius:9px;background:#f0f9ff}.month-suggestions>span{font-size:11px;color:#0369a1;font-weight:700}.month-suggestions>div{display:flex;gap:5px}.month-suggestions button{border:1px solid #7dd3fc;border-radius:7px;background:#fff;padding:6px 10px;color:#0369a1;font-size:12px;font-weight:800;text-transform:capitalize;cursor:pointer}.month-suggestions button:hover{background:#e0f2fe}
        .live-summary{margin-top:14px;border:1px solid #ddd6fe;border-radius:11px;background:#fafaff;overflow:hidden}.live-summary-title{display:flex;align-items:center;gap:7px;padding:10px 12px;background:#f5f3ff;color:#6323ef;font-size:12px}.live-summary-grid{display:grid;grid-template-columns:1fr 1fr}.live-summary-grid>div{display:flex;flex-direction:column;gap:4px;padding:10px 12px;border-top:1px solid #ede9fe}.live-summary-grid>div:nth-child(odd){border-right:1px solid #ede9fe}.live-summary-grid span{font-size:9px;text-transform:uppercase;letter-spacing:.06em;color:#64748b;font-weight:700}.live-summary-grid strong{font-size:11px;line-height:1.4}.live-summary-grid .found{color:#047857}.live-summary-grid .missing{color:#b45309;font-weight:600}@media(max-width:480px){.live-summary-grid{grid-template-columns:1fr}.live-summary-grid>div:nth-child(odd){border-right:0}}
        .live-date-rows{border-top:1px solid #ddd6fe;padding:8px}.live-date-row{display:grid;grid-template-columns:85px 1fr;gap:8px;padding:8px;border-radius:7px;background:#fff;margin-bottom:6px}.live-date-row:last-child{margin-bottom:0}.live-date-row>strong{font-size:11px;text-transform:capitalize;color:#312e81}.live-date-row>div{display:flex;flex-direction:column;gap:4px}.live-date-row span{font-size:10px;color:#047857;line-height:1.35}.live-date-row .missing{color:#b45309}
        .live-summary-title>div{display:flex;flex-direction:column;gap:2px}.live-summary-title>div>span{font-size:9px;color:#64748b;font-weight:500}.who-summary{padding:12px;border-top:1px solid #ede9fe}.who-summary>span,.schedule-preview-label{display:block;margin-bottom:7px;font-size:9px;text-transform:uppercase;letter-spacing:.08em;color:#64748b;font-weight:800}.who-summary>div{display:flex;flex-wrap:wrap;gap:6px}.who-summary strong{display:flex;align-items:center;gap:5px;padding:6px 9px;border-radius:999px;background:#ecfdf5;color:#047857;font-size:11px}.who-summary em,.preview-missing{font-style:normal;color:#b45309;font-size:11px}.schedule-preview{padding:0 12px 12px}.schedule-day{border:1px solid #e2e8f0;border-radius:9px;background:#fff;margin-bottom:8px;overflow:hidden}.schedule-day header{display:flex;justify-content:space-between;align-items:center;padding:9px 10px;background:#f8fafc;border-bottom:1px solid #e2e8f0}.schedule-day header span{font-size:10px;text-transform:uppercase;color:#64748b;font-weight:800}.schedule-day header strong{font-size:11px;text-transform:capitalize}.schedule-blocks{padding:5px 10px}.schedule-block{display:grid;grid-template-columns:auto 14px 1fr;align-items:center;gap:5px;padding:7px 0;border-bottom:1px solid #f1f5f9;font-size:11px}.schedule-block:last-child{border-bottom:0}.preview-time{color:#6323ef;font-weight:900}.arrow{color:#94a3b8;text-align:center}.schedule-block>strong{color:#0f172a}.schedule-block small{grid-column:1/-1;color:#b45309;font-weight:700}.schedule-block.warning{background:#fffbeb;margin:0 -5px;padding:7px 5px;border-radius:6px}.schedule-block.warning .preview-time{color:#b45309}.preview-missing{padding:8px 0}
        .header-actions{display:flex;gap:7px;position:relative}.refresh-chat-button,.fetch-schedule-button{color:#6323ef}.refresh-chat-button:hover,.fetch-schedule-button:hover,.fetch-schedule-button.is-active{background:#f5f3ff;color:#5218d3}.voice-chat-button{color:#6323ef}.voice-chat-button:hover{background:#f5f3ff}.voice-chat-button.is-listening{background:#dc2626;color:#fff;animation:voice-pulse 1.4s infinite}.voice-listening-status{display:flex;align-items:center;gap:8px;margin:0 0 8px;padding:8px 10px;border-radius:9px;background:#fef2f2;color:#b91c1c;font-size:11px;font-weight:700}.voice-listening-status span{width:8px;height:8px;border-radius:50%;background:#dc2626;animation:voice-pulse 1.4s infinite}@keyframes voice-pulse{0%,100%{box-shadow:0 0 0 0 rgba(220,38,38,.3)}50%{box-shadow:0 0 0 6px rgba(220,38,38,0)}}.help-tip{position:absolute;right:76px;top:-4px;width:210px;padding:10px 12px;border:1px solid #c4b5fd;border-radius:10px;background:#fff;box-shadow:0 8px 24px rgba(76,29,149,.16);display:flex;flex-direction:column;gap:3px;z-index:3}.help-tip:after{content:'';position:absolute;right:-7px;top:13px;width:12px;height:12px;background:#fff;border-top:1px solid #c4b5fd;border-right:1px solid #c4b5fd;transform:rotate(45deg)}.help-tip strong{font-size:11px;color:#6323ef}.help-tip span{font-size:10px;line-height:1.35;color:#64748b}.help-tip b{font-size:10px;line-height:1.35;color:#0f172a}@media(max-width:520px){.help-tip{position:fixed;top:68px;left:12px;right:12px;width:auto}.help-tip:after{display:none}}
        .schedule-fetcher input[type="search"]{box-sizing:border-box;width:100%;padding:9px;border:1px solid #cbd5e1;border-radius:8px;font-size:12px}
        .fetch-employees{margin:0;min-width:0;max-height:200px;overflow:auto;padding:8px;border:1px solid #cbd5e1;border-radius:8px}.fetch-employees legend{font-size:11px;font-weight:800;color:#64748b}.schedule-fetcher .fetch-employees label{flex-direction:row;align-items:center;gap:8px;padding:6px 2px;font-size:12px;color:#0f172a;cursor:pointer}.fetch-employees input{width:16px;height:16px;flex:none;accent-color:#6323ef}
        .schedule-fetcher{align-self:stretch;padding:14px;border:1px solid #c4b5fd;border-radius:13px;background:#fafaff;box-shadow:0 8px 22px rgba(76,29,149,.1);display:flex;flex-direction:column;gap:11px}.schedule-fetcher-title{display:flex;align-items:center;gap:9px;color:#6323ef}.schedule-fetcher-title>svg{flex:none}.schedule-fetcher-title>div{display:flex;flex-direction:column;gap:2px}.schedule-fetcher-title strong{font-size:13px}.schedule-fetcher-title span{font-size:10px;color:#64748b}.schedule-fetcher label{display:flex;flex-direction:column;gap:5px;font-size:10px;font-weight:800;color:#64748b}.schedule-fetcher select{box-sizing:border-box;width:100%;height:38px;border:1px solid #cbd5e1;border-radius:8px;background:#fff;padding:0 9px;color:#0f172a;font:inherit;font-size:12px;text-transform:capitalize}.schedule-fetcher-period{display:grid;grid-template-columns:1fr 1fr;gap:8px}.schedule-fetcher-actions{display:flex;justify-content:flex-end;gap:7px}.schedule-fetcher-actions button{border:1px solid #cbd5e1;border-radius:8px;background:#fff;padding:8px 11px;color:#475569;font-size:11px;font-weight:800;cursor:pointer}.schedule-fetcher-actions .fetch-confirm{border-color:#6323ef;background:#6323ef;color:#fff}.schedule-fetcher-actions button:disabled{cursor:not-allowed;opacity:.45}.fetched-schedule{display:flex;align-items:flex-start;gap:10px}.fetched-schedule-content{min-width:0;max-width:85%;padding:12px 14px;border-radius:4px 16px 16px 16px;background:#f1f5f9;display:flex;flex-direction:column;gap:6px}.fetched-schedule-content>strong{font-size:13px}.fetched-schedule-month{font-size:11px;font-weight:800;color:#6323ef;text-transform:capitalize}.fetched-shift{padding-top:6px;border-top:1px solid #e2e8f0;font-size:11px;line-height:1.4;color:#334155;text-transform:capitalize}.fetched-empty{font-size:11px;color:#b45309}
        .copy-schedule-question{display:flex;align-items:flex-start;gap:10px}.copy-schedule-content{min-width:0;max-width:85%;padding:13px 14px;border-radius:4px 16px 16px 16px;background:#f5f3ff;border:1px solid #ddd6fe;display:flex;flex-direction:column;gap:9px}.copy-schedule-content>strong{font-size:13px;color:#4c1d95}.copy-schedule-content>span{font-size:11px;line-height:1.4;color:#64748b}.copy-schedule-period{display:grid;grid-template-columns:1fr 1fr;gap:7px}.copy-schedule-period label{display:flex;flex-direction:column;gap:4px;font-size:9px;font-weight:800;color:#64748b}.copy-schedule-period select{box-sizing:border-box;width:100%;height:34px;border:1px solid #c4b5fd;border-radius:7px;background:#fff;padding:0 7px;font:inherit;font-size:11px;text-transform:capitalize}.copy-schedule-content>button{align-self:flex-end;border:0;border-radius:8px;background:#6323ef;color:#fff;padding:9px 11px;font-size:10px;font-weight:800;cursor:pointer}.copy-schedule-content>button:hover{background:#5218d3}
        .help-tip{width:285px;padding:14px 16px;border-radius:12px;gap:7px;box-shadow:0 10px 30px rgba(76,29,149,.18)}.help-tip strong{font-size:14px}.help-tip span{font-size:12px;line-height:1.5;color:#475569}.help-tip em{font-style:normal;padding:8px 10px;border-radius:7px;background:#f5f3ff;color:#5b21b6;font-size:12px;font-weight:800}.help-tip b{font-size:12px;line-height:1.45}
        .ai-body{flex:1;min-height:0;padding:0;overflow:hidden}.chat-shell{height:100%;display:flex;flex-direction:column;background:#fff}.chat-messages{flex:1;min-height:0;overflow-y:auto;padding:22px;display:flex;flex-direction:column;gap:16px}.chat-welcome{display:flex;align-items:flex-start;gap:10px;padding-top:8px}.chat-welcome>div:last-child{display:flex;flex-direction:column;gap:4px}.chat-welcome strong{font-size:14px}.chat-welcome span{font-size:12px;line-height:1.5;color:#64748b}.user-message{align-self:flex-end;max-width:85%;padding:11px 14px;border-radius:16px 16px 4px 16px;background:#6323ef;color:#fff;font-size:13px;line-height:1.5;white-space:pre-wrap}.conversation-reply{display:flex;align-items:flex-start;gap:10px}.conversation-reply p{max-width:85%;margin:2px 0 0;padding:11px 14px;border-radius:4px 16px 16px 16px;background:#f1f5f9;color:#334155;font-size:13px;line-height:1.5}.thinking-message{display:flex;align-items:center;gap:10px}.thinking-dots{display:flex;align-items:center;gap:4px;height:32px;padding:0 13px;border-radius:16px;background:#f1f5f9}.thinking-dots span{width:6px;height:6px;border-radius:50%;background:#64748b;animation:thinking-pulse 1.2s infinite ease-in-out}.thinking-dots span:nth-child(2){animation-delay:.15s}.thinking-dots span:nth-child(3){animation-delay:.3s}@keyframes thinking-pulse{0%,60%,100%{transform:translateY(0);opacity:.4}30%{transform:translateY(-4px);opacity:1}}.chat-response{margin:0;padding:0;border:0}.chat-response .assistant-response{align-items:flex-start;margin:0;border:0;background:transparent;padding:0}.chat-response .assistant-message{max-width:85%;padding:12px 14px;border-radius:4px 16px 16px 16px;background:#f1f5f9;color:#334155}.composer-area{flex:none;padding:10px 16px 14px;border-top:1px solid #e2e8f0;background:#fff}.chat-composer{position:relative;display:flex;align-items:flex-end;border:1px solid #cbd5e1;border-radius:18px;background:#fff;padding:7px 54px 7px 12px;box-shadow:0 2px 10px rgba(15,23,42,.06)}.chat-composer:focus-within{border-color:#6323ef;box-shadow:0 0 0 3px rgba(99,35,239,.1)}.chat-composer textarea{min-height:105px;max-height:210px;width:100%;resize:none;border:0;border-radius:0;padding:12px 0;background:transparent;font-size:13px;line-height:1.45}.chat-composer textarea:focus{border:0;box-shadow:none}.chat-composer .interpret-button{position:absolute;right:7px;bottom:7px;width:38px;height:38px;margin:0}.chat-composer .interpret-button:disabled{cursor:not-allowed;opacity:.4;box-shadow:none}.composer-area .hint{text-align:center;margin:7px 0 0}.composer-area .matched-employee,.composer-area .matched-project,.composer-area .employee-suggestions,.composer-area .project-suggestions,.composer-area .time-suggestion,.composer-area .month-suggestions{margin:0 0 8px}.assistant-message .approve-chat{align-self:flex-start;width:auto;padding:10px 14px}.chat-messages .error,.chat-messages .success{margin-top:0}
        .schedule-employees{display:flex;flex-wrap:wrap;gap:5px 10px;align-items:center;margin-bottom:2px}.schedule-pass-line{display:flex;align-items:center;gap:5px}.schedule-conflict-icon{flex:none;color:#dc2626;width:13px;height:13px;cursor:help}.schedule-chat-actions{display:flex;justify-content:flex-end;gap:5px;margin-top:20px}.schedule-chat-actions .approve-chat{flex:0 1 auto;width:auto;margin:0;padding:7px 9px;border-radius:7px;font-size:10px}.edit-chat,.undo-chat{border:1px solid #cbd5e1;border-radius:7px;padding:7px 9px;background:#fff;color:#475569;font-size:10px;font-weight:800;cursor:pointer}.undo-chat{display:grid;place-items:center;width:29px;padding:0}.edit-chat:hover,.undo-chat:hover:not(:disabled){border-color:#6323ef;color:#6323ef}.edit-chat:disabled,.undo-chat:disabled{cursor:not-allowed;opacity:.35}.inline-schedule-editor{display:flex;flex-direction:column;gap:10px;min-width:230px}.inline-schedule-editor label{display:flex;flex-direction:column;gap:4px;color:#64748b;font-size:10px;font-weight:800}.inline-schedule-editor input,.inline-schedule-editor select{box-sizing:border-box;width:100%;height:36px;border:1px solid #cbd5e1;border-radius:7px;background:#fff;padding:0 8px;color:#0f172a;font:inherit;font-size:12px}.inline-employees-editor{display:flex;flex-direction:column;gap:6px}.inline-editor-heading{display:flex;align-items:center;justify-content:space-between;color:#64748b;font-size:10px;font-weight:800}.inline-employee-row{display:grid;grid-template-columns:1fr 30px;gap:6px}.inline-employee-row select{min-width:0}.add-edit-employee,.remove-edit-employee{border-radius:7px;display:grid;place-items:center;cursor:pointer;font-weight:800}.add-edit-employee{width:28px;height:28px;border:1px solid #c4b5fd;background:#f5f3ff;color:#6323ef;font-size:18px}.remove-edit-employee{width:30px;height:36px;border:1px solid #fecaca;background:#fff1f2;color:#dc2626;font-size:18px}.add-edit-employee:disabled,.remove-edit-employee:disabled{cursor:not-allowed;opacity:.35}.inline-edit-row{display:flex;flex-direction:column;gap:7px;padding-top:9px;border-top:1px solid #cbd5e1}.inline-edit-block{position:relative;display:grid;grid-template-columns:1fr 1fr;gap:7px;padding-top:28px}.inline-project-field{grid-column:1/-1}.delete-edit-block{position:absolute;top:0;right:0;width:24px;height:24px;border:1px solid #fecaca;border-radius:6px;background:#fff1f2;color:#dc2626;display:grid;place-items:center;cursor:pointer}.delete-edit-block svg{width:10px;height:10px}.delete-edit-block:hover:not(:disabled){background:#fee2e2;border-color:#f87171}.delete-edit-block:disabled{cursor:not-allowed;opacity:.35}
        .copy-date-warning{display:flex;align-items:flex-start;gap:7px;margin:7px 0;padding:9px 10px;border:1px solid #fca5a5;border-radius:8px;background:#fef2f2;color:#b91c1c;font-size:10px;line-height:1.45}.copy-date-warning>svg{flex:none;width:13px;height:13px;margin-top:1px}.copy-date-warning span,.copy-date-warning strong{color:#b91c1c}.copy-date-warning strong{font-size:10px}
        .resize-handle{position:absolute;left:-14px;top:50%;transform:translateY(-50%);width:28px;height:70px;border:1px solid #d8b4fe;border-radius:12px;background:#fff;box-shadow:-3px 2px 12px rgba(76,29,149,.16);z-index:10002;cursor:ew-resize;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;padding:0;touch-action:none}.resize-handle span{display:block;width:3px;height:3px;border-radius:50%;background:#7c3aed}.resize-handle:hover,.ai-panel.is-resizing .resize-handle{background:#f5f3ff;border-color:#8b5cf6}.ai-panel.is-resizing{transition:none}.ai-panel{min-width:400px;max-width:calc(100vw - 24px)}@media(max-width:480px){.ai-panel{width:100vw!important;min-width:0;max-width:100vw}.resize-handle{display:none}}
      `}</style>
    </aside>
  );
}
